/**
 * Data layer for the Request Correspondence panel (#1069).
 *
 * Owns every provider call so the panel stays presentational. Since Story
 * 2.3 (D8) there is no provider "session" to own: `connect`/`disconnect`
 * modelled one a first-party API never had, and the flag they drove gated
 * three fetch effects. What decides whether this hook fetches is now exactly
 * `enabled`, `openThreadId` and the viewer's id, and nothing else (pinned by
 * the load-timing test).
 *
 * MEMBERSHIP IS AUTOMATIC. Every thread is created with the full membership of
 * its space, taken from the request's real team. Nobody is invited to a thread
 * individually, so a thread's audience is always exactly its space's audience.
 *
 * Errors are surfaced, never swallowed. A vendor failing here IS the finding.
 */
import {
  hashKey,
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { isMentionNotAllowedBody } from './correspondence.types';
import {
  appendMessage,
  correspondenceMessagesKeys,
  correspondenceThreadFilesKeys,
  correspondenceThreadsKeys,
  createThreadMutation,
  deleteMessageMutation,
  editMessageMutation,
  foldNewestPage,
  inFlightPostsOptions,
  listKeyInheritableAcrossSpace,
  mapMessages,
  messagesInReadingOrder,
  messagesKeyHasNoThread,
  messagesQuery,
  olderCursorOf,
  pageCoversScreen,
  postMessageMutation,
  renameThreadMutation,
  threadFilesQuery,
  threadsQuery,
  toPendingRows,
} from './correspondenceQueries';
import { isKeptOffline, keptOfflineFor } from './keptOffline';
import { getMessagingProvider, type IMessagingProvider } from './providers';
import { type SpaceMember } from './team';
import * as uploadOverlay from './uploadOverlay';
import {
  isReadOnlyViolationError,
  READ_ONLY_VIOLATION_MESSAGE,
} from '../../services/impersonationService';

import type {
  Message,
  PendingMessage,
  SpaceId,
  Thread,
  ThreadFileListing,
} from './correspondence.types';
import type {
  DeleteMessageVariables,
  EditMessageVariables,
  MessagePages,
  MessagesKey,
  PostMessageVariables,
  ThreadFilesKey,
} from './correspondenceQueries';
import type { OutgoingMessageFile } from './providers/provider.types';

/**
 * ⚠️ THE FOUR WRITERS LEFT IN STORY 6.5. `createThread`, `postMessage`,
 * `editMessage` and `deleteMessage` were four hand-written `try/catch` blocks
 * over direct provider calls; they are four `useMutation`s now, defined in
 * `correspondenceQueries.ts` with their options and their reasons, and the
 * mutation cache is the library's record of every correspondence write in
 * flight. The public members keep their names, signatures and outcome mapping:
 * they are wrappers over `mutateAsync`. Two options are deliberately NOT the
 * house default and the header there says why -- `retry: false` (there is no
 * idempotency key, so a retry would deliver a message twice) and
 * `networkMode: 'always'` (the default PAUSES a mutation offline and fires it
 * on reconnect, which is a replay path this product withdrew).
 *
 * The state this hook still OWNS.
 *
 * ⚠️ `threads`, `threadsKey` and `loadingThreads` LEFT IN STORY 6.3, AND
 * `messages`, `olderMessagesCursor`, `loadingMessages` AND `loadingOlder` LEFT
 * IN STORY 6.4. Both lists are React Query queries now
 * (`correspondenceQueries.ts`) and all seven members are DERIVED from their
 * state on every render; every one of them is still on the returned object,
 * unchanged in name and type, because the panel reads them. What left is the
 * hand-rolled TRACKING — the `useState` slots, the effect that loaded the
 * newest page, the cursor ref, the in-flight ref keyed by thread, the refs
 * that decided whose flight was whose, and the merge closure over this
 * interface.
 *
 * What stays here is the optimistic rows and the last refusal, plus `error`,
 * which is one slot shared by every writer and read beside the two queries'
 * own errors.
 *
 * ⚠️ `pending` LEFT IN STORY 6.6, AND WITH IT THE LAST COPY OF SERVER STATE IN
 * THIS FILE. A pending row is a JOIN of two kinds of truth: WHICH posts are in
 * flight, which the mutation cache records per mutation, and HOW FAR ALONG each
 * one is, which the cache has no slot for (`mutation.js:56-64`: the fn context
 * carries no progress channel and variables never change). The first half is
 * `useMutationState(inFlightPostsOptions())`, the second is `uploadOverlay`,
 * and `toPendingRows` joins them in RENDER. What this hook still owns is
 * `writeRefusal` and `error`, and nothing else.
 *
 * ⚠️ THE OPEN-THREAD FILTER IS IN RENDER, NOT IN THE FILTERS. `useMutationState`
 * recomputes only inside its `mutationCache.subscribe` callback
 * (`useMutationState.js:20-46`) and a thread switch fires no mutation event, so
 * a predicate reading the open thread would leave A's rows under B's header.
 *
 * ⚠️ AND THE OVERLAY IS KEYED ON THE `QueryClient`, NOT HELD HERE. The docked
 * panel UNMOUNTS on close (`CorrespondenceDock.tsx:342-345`) while its upload
 * keeps going; a `useState` map here would die with it and the reopened panel
 * would show a bar stuck at 0% and an inert Cancel. `uploadOverlay`'s header
 * has the rest.
 *
 * TWO THINGS A USER CAN SEE FOLLOW FROM THAT, and both are the point: a row
 * survives a thread switch AWAY AND BACK, and it survives the docked panel
 * being closed and reopened mid-upload.
 *
 * (History: this paragraph used to say why `pending` was still hand-held after
 * Story 6.5, and that 6.5 left it one deletion away. It did.)
 *
 * ⚠️ THE SEAM, AND IT IS `patch(`, NOT `setQueryData` (code review,
 * 2026-09-17). A writer cannot reach the conversation except through a
 * `MessagePages` updater, so "where does a write touch the open conversation"
 * has exactly one answer a grep can find. It is `patch(`: a `setQueryData`
 * grep does NOT find the writers -- its four hits are the two inside `patch`
 * itself and two that write the THREADS key (the unread-row clear), and the
 * count coincidentally matching "four" is how the wrong grep survived review.
 * Since Story 6.5 those four call sites are reached from the writers'
 * `onSuccess` callbacks rather than from inline code after an `await`.
 */
interface State {
  /**
   * The server's own explanation for the LAST refused write, when it was a
   * refusal the user can act on (a 403's reason, a 400's reason; Story 1.3,
   * AC 37), or `null`. Set beside `error` on a failed write and cleared when
   * the next write starts. The panel prefers it over the generic unsent copy.
   */
  writeRefusal: string | null;
  /**
   * The ids a `400 MENTION_NOT_ALLOWED` refused on the last write (#1599
   * Story 1.4, AC 9), `[]` otherwise. Set and cleared WITH `writeRefusal`
   * (one setter), so the outlined tokens and the sentence that explains them
   * can never disagree.
   */
  refusedMentionIds: readonly string[];
  error: string | null;
  // ⚠️ THERE IS DELIBERATELY NO `unsentFileCount` HERE (code review
  // 2026-09-13). There was one, with a docblock saying it existed so the unsent
  // line could name the files — and nothing in `src/` ever read it. AC 13's
  // count is the panel's, captured at the moment of the post, because the panel
  // owns the staged files and renders the line. One source of truth for one
  // sentence; see `CorrespondencePanel.post`.
  //
  // ⚠️ CORRECTED AT RUN 2, TWICE OVER. An earlier version of this note added
  // "which is how the live one came to be the wrong quantity as well" — wrong,
  // and the story retracts it; the rendered count was never live. It was also
  // written as a `/** */` block, which attaches to the NEXT member, so a note
  // about a field that does not exist was hovering on `olderMessagesCursor`.
  // A note about an absence is a line comment.
}

/**
 * What a loaded `threads` array describes — `requestId|space` — or `null` when
 * the rows on screen are not this request's and this space's.
 *
 * ⚠️ IT EXISTS SO THE PANEL CAN TELL "I HAVE THE LOCAL ANSWER" FROM "I HAVE
 * SOMEBODY ELSE'S ANSWER" (#1069 P3.3). The rows are deliberately NOT blanked
 * while the next listing runs — that would empty the list on every tab press —
 * so they can be describing the space the user has just left. Anything that
 * reads them as a statement about the CURRENT space is wrong for that window,
 * and the tab badge is exactly such a reader.
 *
 * ⚠️ THE REQUEST ID IS IN THE KEY, NOT JUST THE SPACE. A first version carried
 * the space alone; review pointed out that the listing is keyed on `requestId`
 * too, so a request change left a matching space and another request's rows.
 * Since Story 6.3 the rows themselves no longer survive a request change
 * (`placeholderData` refuses to inherit across one), so this guard now has a
 * structural partner rather than carrying the hazard alone.
 *
 * Exported so the panel compares the same string this hook derives, rather
 * than rebuilding the format. It is still a FIELD on the surface — the panel
 * reads it at the two gates that keep the tab badge honest — but since Story
 * 6.3 nothing WRITES it: it is computed from the query's own flags.
 */
export const threadsKeyFor = (requestId: string, space: SpaceId): string =>
  `${requestId}|${space}`;

const NO_REFUSED_IDS: readonly string[] = Object.freeze([]);

const INITIAL: State = {
  writeRefusal: null,
  refusedMentionIds: NO_REFUSED_IDS,
  error: null,
};

/**
 * ⚠️ ONE ARRAY, NOT A FRESH `[]` PER RENDER. `threads` is derived on every
 * render, and a new empty array each time would give the panel a new prop
 * identity on every render of a request that has no conversations — which is
 * exactly the identity-not-contents dependency that once cost ~2,600 requests
 * (see `correspondenceQueries.ts`'s header). Frozen so a caller that mutates
 * it fails loudly here rather than corrupting every future empty list.
 */
const EMPTY_THREADS = Object.freeze([]) as unknown as Thread[];

/** The same one-array rule as `EMPTY_THREADS`, for the open conversation. */
const EMPTY_MESSAGES = Object.freeze([]) as unknown as Message[];

/**
 * The one error `loadOlderMessages` writes, so its success path can clear it.
 * Exported (Story 7.6 review, 2026-09-19) so the panel can tell the jump's
 * pull that a page FAILED rather than repeated; not a member of the hook's
 * surface.
 */
export const OLDER_PAGE_ERROR_PREFIX = 'loading earlier messages: ';

/**
 * The sequence behind every `localId`. Module-scoped deliberately -- see the
 * warning at its use site in `postMessage`.
 */
let nextLocalIdSeq = 0;
// ⚠️ `isOlderPageError` WENT WITH THE SHARED SLOT (Story 6.4). It
// existed so `loadOlderMessages`'s success path could clear ITS OWN earlier
// failure out of an `error` slot it shared with posting and listing. The
// derivation asks the query which error it is holding
// (`isFetchNextPageError`), so there is nothing to tell apart by prefix. The
// PREFIX stays: it is the user-visible text, and a pin matches on it.

/**
 * ⚠️ CALL THIS AT THE TOP OF THE CATCH, NEVER INSIDE A `setState` UPDATER.
 * Updaters run later, during React's next render. With Metro's default
 * transform profile (`async` to a Babel `asyncToGenerator` generator, arrows
 * to `function` expressions) Hermes under LAZY compilation (dev builds on a
 * device) loses the `catch (e)` binding for a closure created inside the
 * catch and invoked after the generator has resumed: `ReferenceError:
 * Property 'e' doesn't exist`, surfacing as a Render Error in whatever
 * component happens to be rendering. Reproduced 2026-09-09 by running the
 * served bundle's `reloadThreads` under `hermes -lazy` (throws) and under
 * `hermes` / `hermes -O` (pass), which is why release builds and Jest never
 * showed it; the hoisted shape below was then run under `-lazy` and passes.
 * The `hermes-stable` profile, which keeps native arrows, did not reproduce.
 * Each catch therefore reads the message into a local first and captures
 * the local. Pinned by the lazy-Hermes guard in `useCorrespondence.test.ts`.
 */
const describe = (e: unknown): string =>
  e instanceof Error ? e.message : String(e);

/**
 * The server's explanation, when the failure is a refusal the user can act
 * on. `ApiError` carries the status and the server's `error` string as its
 * message (`src/services/api.ts`); duck-typed on `status` so this module never
 * loads the API client. Anything else (a 500, a network failure) is `null` and
 * the panel shows its generic copy.
 */
const refusalText = (e: unknown): string | null => {
  const status = (e as { status?: unknown } | null)?.status;
  return (status === 403 || status === 400) && e instanceof Error
    ? e.message
    : null;
};

/**
 * The ids a mention refusal named (#1599 Story 1.4), read off the parsed body
 * `ApiError` (text path) and `UploadError` (files path) both carry. Duck-typed
 * on `body` for the reason `refusalText` is duck-typed on `status`.
 */
const refusedMentionIdsOf = (e: unknown): readonly string[] => {
  const body = (e as { body?: unknown } | null)?.body;
  return isMentionNotAllowedBody(body) ? body.refused : NO_REFUSED_IDS;
};

/**
 * What became of a post (#1069 Story 4.1, AC 11 vs AC 13; code review
 * 2026-09-13).
 *
 * ⚠️ THREE OUTCOMES, NOT TWO. This was `boolean`, and a CANCEL and a FAILURE
 * both came back `false`, so the panel printed AC 13's "Not sent. Your message
 * and 3 files are still here" at a user who had just pressed Cancel — the one
 * thing AC 11 says must not happen. The hook always knew the difference
 * (`UploadError.aborted`); the return type could not carry it.
 *
 * ⚠️ AND FOUR, NOT THREE (#1069 Story 6.7, D2 to D4). A files upload whose
 * last byte has LEFT the device and then hears nothing back is not a failure
 * the panel may call "Not sent": the server may be inside its transaction,
 * and "Not sent" would be the guess that delivers the message twice.
 * `'unconfirmed'` is that outcome. The transport reports it
 * (`UploadError.unconfirmed`), this hook reconciles exactly as a cancel does,
 * and the panel prints its own line and cautions on the strip.
 */
export type PostOutcome = 'sent' | 'cancelled' | 'failed' | 'unconfirmed';

/**
 * What `createThread` hands back (#1530 review, C1). It used to be the id or
 * `null`, and `null` could not say WHICH failure: a first message that carries
 * files rides the progress transport, whose response-phase stall means the
 * conversation may already exist. That is `'unconfirmed'`, mapped exactly as
 * `postMessage` maps it; the hook reloads the listing (the conversation may be
 * in it) and the panel keeps the compose form and prints the cautious line.
 */
export type CreateThreadResult =
  | { outcome: 'sent'; id: string }
  | { outcome: 'failed' }
  | { outcome: 'unconfirmed' };

/** An edit's outcome: a `PostOutcome` that is never `'cancelled'` (no pending row). */
export type EditOutcome = Exclude<PostOutcome, 'cancelled'>;

export interface UseCorrespondence extends State {
  /**
   * The viewer's own posts awaiting the server's reply (Story 1.3, AC 17),
   * rendered after `messages` as own-side rows. Not a flag on `Message`: the
   * wire type is pinned key-for-key across the tiers (C11).
   *
   * ⚠️ DERIVED SINCE STORY 6.6, not state. Which posts are on the wire is the
   * MUTATION CACHE's record (`useMutationState` over the post key), how far
   * along each one is, is `uploadOverlay`'s, and `toPendingRows` joins the two
   * and keeps the open thread's. Two consequences the panel may rely on: a row
   * SURVIVES a switch to another conversation and back, and it survives the
   * docked panel being closed and reopened while the upload runs. `[]` with no
   * thread open, because no row's thread matches `null`.
   */
  pending: PendingMessage[];
  /**
   * The active space's conversations. Since Story 6.3 these are the query's
   * `data` — `[]` while it has none and `[]` when the listing FAILED, because
   * a list the server has just refused must not be rendered as "nothing here".
   */
  threads: Thread[];
  /** `threadsKeyFor(requestId, space)`, or `null`. See its docblock. */
  threadsKey: string | null;
  /**
   * A listing is on the wire — ANY listing, the first or a reload. Not "the
   * first load": the panel's open-thread refresh gate needs "is something in
   * flight", and its spinner is separately gated on an empty list.
   */
  loadingThreads: boolean;
  /**
   * The open conversation, in reading order: oldest first, newest at the
   * bottom (FR66).
   *
   * ⚠️ DERIVED FROM THE MESSAGES QUERY'S `data` SINCE STORY 6.4, not state.
   * The provider pages BACKWARDS, so the cache holds the newest page first and
   * each page newest-first; `messagesInReadingOrder` flips both levels. `[]`
   * while there is no thread open, while the first page is loading, and when
   * the first load FAILED -- a conversation the server has just refused must
   * not be rendered as "nothing here", the same rule `threads` follows.
   */
  messages: Message[];
  /**
   * Every file of the open conversation, newest message first and upload
   * order within a message (#1069 Story 7.4, FR77), or `null`.
   *
   * ⚠️ `null` MEANS "NOTHING TO SAY", AND IT COVERS THREE CASES ON PURPOSE:
   * no conversation is open, the listing is still on the wire, and the
   * listing FAILED. The panel renders the Files row only for a listing with
   * items, so all three are the same absent row — a failed listing is silent
   * (D8), because the bubbles underneath still show every one of these files
   * and a banner over a working conversation would say more than went wrong.
   * Whether it should say so is the owner's, and is in `deferred-work.md`.
   *
   * ⚠️ NOT LAZY. The listing is fetched when the conversation opens, because
   * the count IS the list's own (owner, 2026-09-17); only the strip's open
   * state waits for a press.
   */
  threadFiles: ThreadFileListing | null;
  /**
   * The provider's cursor for messages OLDER than the ones on screen, or null
   * when the beginning of the thread has been reached. `loadOlderMessages`
   * (Story 1.2) consumes it; `MessageList` shows its control while it is set.
   *
   * ⚠️ DERIVED SINCE STORY 6.4: `hasNextPage ? olderCursorOf(data) : null`.
   * `hasNextPage` is the library's own end signal
   * (`getNextPageParam(lastPage) != null`), and the two are equal by
   * construction. It goes `null` SYNCHRONOUSLY on a thread switch, because a
   * key change publishes an optimistic pending result before any fetch.
   */
  olderMessagesCursor: string | null;
  /**
   * The FIRST page of the open conversation is on the wire.
   *
   * ⚠️ DERIVED SINCE STORY 6.4: `isLoading`, which is `isPending && isFetching`
   * -- so it is `false` with no thread open (the query is disabled), `false`
   * after a failed first load, and `false` during a post's re-walk over rows
   * that are already on screen. All three are what the hand-written field did;
   * only the open-thread effect ever set it.
   */
  loadingMessages: boolean;
  /**
   * An older page is in flight (`loadOlderMessages`).
   *
   * ⚠️ DERIVED SINCE STORY 6.4: `isFetchingNextPage`, which the observer
   * distinguishes from a re-walk by the fetch's own `fetchMeta.fetchMore`. A
   * post's re-fetch is not "older".
   */
  loadingOlder: boolean;
  provider: IMessagingProvider;
  reloadThreads: () => Promise<void>;
  openThread: (threadId: string | null) => void;
  openThreadId: string | null;
  /**
   * Start a conversation WITH its first message (Story 1.3, D1): one provider
   * call, then a reload of the listing (awaited inside the mutation's
   * `onSuccess` since Story 6.5, so the row is listed before the id comes
   * back), then the new id (the panel opens it
   * and the message effect loads the page that holds the first message).
   * `{ outcome: 'failed' }` on a refusal, with the server's text in `error`;
   * `{ outcome: 'unconfirmed' }` when a files upload left the device and the
   * server never answered (see `CreateThreadResult`).
   */
  createThread: (
    title: string,
    body: string,
    /** Inline images on the first message (#1530). */
    files?: OutgoingMessageFile[],
  ) => Promise<CreateThreadResult>;
  /**
   * `true` when the provider accepted the message, `false` when it did not.
   *
   * It used to swallow the failure into `state.error` and return `void`, which
   * left the panel unable to tell a send that worked from one that did not —
   * so it cleared the composer either way. A caller that must keep a pasted
   * email on failure needs the answer, not just a banner somewhere.
   *
   * Since Story 1.3 the message renders OPTIMISTICALLY as a pending row on
   * the viewer's side before the server confirms (AC 17); on success the
   * server's message replaces it, on failure it disappears and the draft is
   * still in the composer.
   *
   * ⚠️ SINCE STORY 6.5 THIS IS A WRAPPER OVER `mutateAsync`, and awaiting it
   * still means what it always meant: the library AWAITS `onSuccess` before
   * the promise resolves (`mutation.js:123-144`), so the optimistic row is
   * gone, the message is in the cache and the re-walk has finished by the time
   * the caller continues.
   */
  postMessage: (
    threadId: string,
    body: string,
    files?: OutgoingMessageFile[],
  ) => Promise<PostOutcome>;
  /**
   * Call off a post in flight (#1069 Story 4.1, AC 11, FR18).
   *
   * ⚠️ WHERE THE HANDLE COMES FROM SINCE STORY 6.5 (AC 8): the post is a
   * mutation, and its mutation function calls `onUploadStarted` with the abort
   * handle BEFORE it returns the upload promise
   * (`correspondenceQueries.ts`, `postMessageMutation`); the public
   * `postMessage` wrapper's `onUploadStarted` is what puts it in the hook's
   * `abortsRef` under this `localId`. That ordering is the point: a cancel
   * pressed in the first moments of an upload must find a handle. Cancelling
   * makes the upload reject with `aborted: true`, which the post's `onError`
   * turns into a silent removal and a reconcile.
   *
   * ⚠️ AND IT IS NOT PROOF NOTHING WAS WRITTEN. `xhr.abort()` tears down this
   * end only; see the implementation and `.claude/rules/correspondence.md`.
   */
  cancelPending: (localId: string) => void;
  /**
   * Correct the viewer's own message (Story 1.3, AC 33). Replaces it in place
   * on success (`'sent'`); `'failed'` with the server's text in `error`
   * otherwise. Under the same open-thread and generation guards as the other
   * writers. `'unconfirmed'` (#1530 review, C1) is the files-edit twin of the
   * post's fourth outcome: the PATCH body left the device and nothing came
   * back, so the edit may have landed; the open thread is reconciled exactly
   * as a cancelled post is, and the panel prints the cautious line.
   */
  editMessage: (
    threadId: string,
    messageId: string,
    body: string,
    /** Inline images ADDED by the edit (#1530). */
    files?: OutgoingMessageFile[],
  ) => Promise<EditOutcome>;
  /**
   * Withdraw a message (Story 1.3, AC 33). On success the row becomes the
   * tombstone the server would return (`deleted: true`, empty body, no
   * `editedAt`); `false` with the server's text in `error` otherwise.
   */
  deleteMessage: (threadId: string, messageId: string) => Promise<boolean>;
  /**
   * Rename the conversation (Story 7.3, FR74). `true` when the server took it;
   * `false` with the server's text in `error` and in `writeRefusal` otherwise.
   *
   * ⚠️ IT RESOLVES ONLY ONCE THE NEW TITLE IS ON SCREEN. `onSuccess` awaits the
   * threads-key invalidation (D7), and the library awaits `onSuccess` before
   * `mutateAsync` resolves, so by the time this resolves `threads` carries the
   * new title. There is no optimistic title and no local copy of one: the
   * header reads `openThread.title` off the threads query.
   *
   * ⚠️ IT TOUCHES NO MESSAGE PAGE. A rename is not a message, so nothing
   * `patch(`es `correspondenceMessagesKeys`; `messages` is referentially equal
   * across a rename.
   */
  renameThread: (threadId: string, title: string) => Promise<boolean>;
  /**
   * Fetch the page OLDER than the ones on screen and PREPEND it (Story 1.2,
   * AC 27, 35). A no-op when there is no cursor (the beginning has been
   * reached) or an older page is already in flight for this thread.
   *
   * ⚠️ SINCE STORY 6.4 THE CURSOR, THE COLLAPSE AND THE DROP ARE THE
   * LIBRARY'S. `fetchNextPage` hands the last page's `nextCursor` to the
   * fetch itself -- this hook never reads a cursor to BUILD a request, only to
   * decide whether there is anything to ask for. A page that resolves after
   * the user left the thread lands on the previous KEY, which the switch
   * effect has already removed, so nothing is written anywhere; that is what
   * the generation guard did by hand.
   */
  loadOlderMessages: () => Promise<void>;
  /**
   * Fetch the open conversation's NEWEST page again and fold it in (Story
   * 2.3, AC 7; D2, D6). Called by the panel after a clock-driven re-listing
   * shows the open thread's own `lastActivityAt` moved: a reply, an edit or
   * a withdrawal by somebody else appears without a press, WITHIN THE NEWEST
   * PAGE. `mergeNewestPage` keeps loaded older history, so a change above
   * the page is not seen here (code review 2026-09-11, review decision 1); the generation
   * and open-thread guards drop a page that resolves after a thread switch;
   * a failure keeps the list and reports nothing (a LATER move asks again;
   * that particular move is not retried). The thread is marked read ONLY
   * WHEN THE PAGE COVERED EVERYTHING ON SCREEN (the page replaced the list,
   * or started at its first message): then what the page brought was ON
   * SCREEN (FR56, spec 7.3 "opening clears it"), the same rationale as
   * `postMessage`'s re-fetch. With older history kept above the page the
   * dot is left alone, because a corrected body up there is exactly what
   * the reader has NOT seen. Then `onThreadRead` and the row's local clear,
   * exactly as the open-thread effect does. Overlapping refreshes of one
   * thread collapse, as `loadOlderMessages` does.
   *
   * ⚠️ IT STAYS A DIRECT `listMessages` AND A FOLD, AND DOES NOT BECOME AN
   * INVALIDATION (Story 6.4, C7). `refetch` on an infinite query re-walks
   * EVERY loaded page in sequence (`infiniteQueryBehavior.js:64-73`), so a
   * reader who has pressed "Load earlier" three times would pay four requests
   * per clock move where today's costs one -- and it would refresh the history
   * above the page, which review decision 1 says must NOT be marked read. One
   * request, one fold, whatever the reader has loaded.
   */
  refreshOpenThread: () => Promise<void>;
  /**
   * Refetch the OPEN conversation's files listing (#1069 offline state). The
   * panel's reconnect needs it: a conversation entered offline failed its
   * listing, and `refreshOpenThread` only invalidates it when the newest page
   * carries a file, so the Files row would otherwise never come back. Resolves
   * when the refetch settles; a failure is the listing's own (silent, D8).
   */
  reloadThreadFiles: () => Promise<void>;
}

export const useCorrespondence = (
  requestId: string,
  viewer: SpaceMember | null,
  members: SpaceMember[],
  space: SpaceId,
  enabled: boolean,
  /**
   * Fired after a thread has been marked read, so the SCREEN can refresh the
   * indicators that outlive this hook (#1069 P3.2).
   *
   * ⚠️ IT STAYS A CALLBACK, AND STORY 6.3 DECIDED SO DELIBERATELY
   * (2026-09-16, D4). It was a callback originally because this directory's
   * suites could not mount a `QueryClientProvider`; Story 6.1 removed that
   * constraint, so the question was reopened and answered on its merits.
   *
   * It does NOT become an invalidation of the thread list. The screen's own
   * fan-out (`useRequestCorrespondenceState`) already invalidates the summary,
   * the request list and the offline lists; adding the thread key would issue
   * a LISTING on every thread open, on top of the four limited requests per
   * clock move the summary's rate-limit arithmetic already counts. The panel's
   * own row is instead cleared locally and immediately via `setQueryData`,
   * for the reason the open-thread effect gives: a round trip would put a
   * visible delay on the one interaction that must feel settled.
   *
   * Optional, and absence is silent — the panel still works, the badge is just
   * stale until the next natural refetch.
   */
  onThreadRead?: () => void,
  /**
   * Whether the DEVICE reports a connection (#1069 offline state). Only read
   * to decide what `threads` shows after a failed listing; see there.
   */
  deviceOnline = true,
  /**
   * The open conversation and its mark-read token, CONTROLLED by a caller
   * that outlives this hook (#1069, 2026-09-25,
   * `spec-1069-panel-survives-resize.md`). The panel passes its session's,
   * so the conversation survives the dock/overlay swap and the docked close,
   * both of which remount the panel and therefore this hook. Every effect
   * below keys on the VALUE, never on who owns it. The token travels too, or
   * each remount onto an already-open thread would mark it read again (one
   * POST and one screen refetch per threshold crossing). Omitted, the hook
   * holds both itself, which is what its own suite exercises.
   */
  controlled?: {
    openThread: readonly [
      string | null,
      Dispatch<SetStateAction<string | null>>,
    ];
    /**
     * The SAME thread, read live (2026-09-26 review). A post, edit or delete
     * started in a mount a resize retired still completes, and its "is the
     * user still looking at this thread?" checks must read the thread the
     * session holds NOW, not the retired mount's last committed copy.
     */
    openThreadNow: { current: string | null };
    markedToken: { current: string | null };
    /** The open thread's cache keys as last committed; see the removal effect. */
    threadCacheKeys: {
      current: {
        messages: readonly unknown[];
        files: readonly unknown[];
      } | null;
    };
    /**
     * The request's correspondence was revoked (the panel's `accessRevoked`).
     * A leave that a revoke caused removes the conversation's entries even
     * offline; see the removal effect.
     */
    accessRevoked?: boolean;
    /** Every change to `writeRefusal`, from whichever mount made the write. */
    onWriteRefusal: (refusal: string | null) => void;
    /**
     * Every change to `refusedMentionIds` (#1599 Story 1.4), beside
     * `onWriteRefusal` and for the same reason: the refusal can land in the
     * mount a resize just retired.
     */
    onRefusedMentionIds?: (ids: readonly string[]) => void;
  },
): UseCorrespondence => {
  const provider = useMemo(() => getMessagingProvider(), []);

  /**
   * ⚠️ HELD IN A REF SO IT CANNOT RE-KEY THE MESSAGE EFFECT. That effect's
   * dependency array is `[openThreadId, provider, viewerId]`; adding a
   * callback a parent re-creates on every render would re-run the whole
   * message load — and the mark-read inside it — on every parent render.
   */
  const onThreadReadRef = useRef(onThreadRead);
  onThreadReadRef.current = onThreadRead;

  const [state, setState] = useState<State>(INITIAL);
  // ⚠️ THERE IS DELIBERATELY NO `messagesRef` HERE ANY MORE (Story 6.4, D8).
  // It existed because a `setState` updater does not run when it is queued, so
  // "was the row on screen?" could not be answered from inside one. The cache
  // answers it directly and synchronously -- `getQueryState(key)?.data` is the
  // same snapshot the fold is about to be applied to -- which also closes the
  // `messagesRef`-vs-queued-state gap the 2026-09-11 second pass recorded.
  const localOpenThread = useState<string | null>(null);
  const [openThreadId, setOpenThreadId] =
    controlled?.openThread ?? localOpenThread;

  /**
   * The open thread, readable from a callback that has already awaited.
   *
   * `postMessage`'s re-fetch (below) resolves after two round trips, by which
   * time the user may have opened a different thread. A closure over the
   * `openThreadId` STATE would be stale in exactly that case and would paste
   * one thread's messages into another's view. The ref is the current value,
   * not the one that existed when the callback was built.
   */
  // Seeded with the mount's value, not `null`: a controlled thread that is
  // already open when this hook mounts (a resize, a docked reopen) must not
  // read as "no thread" to a callback that runs before the first effect.
  const localOpenThreadIdRef = useRef<string | null>(openThreadId);
  // Controlled, the session's own live view: it is written by event handlers
  // and effects only (never during render), so it is always a committed value,
  // and it keeps answering for a mount that has been retired.
  const openThreadIdRef = controlled?.openThreadNow ?? localOpenThreadIdRef;
  const controlledThread = controlled !== undefined;
  // ⚠️ ASSIGNED IN AN EFFECT, NOT DURING RENDER. A ref mutated in the render
  // body is written by renders React may discard (StrictMode's double
  // invocation, an interrupted concurrent render), so the guard could compare
  // against a thread id that was never committed. Committed value only.
  useEffect(() => {
    if (controlledThread) return;
    localOpenThreadIdRef.current = openThreadId;
  }, [openThreadId, controlledThread]);

  /**
   * Whether THIS mount is still mounted. A mutation's callbacks outlive the
   * mount that started it (a resize, a docked close), and one of them, the
   * post's re-walk, cancels any in-flight fetch of the thread: from a retired
   * mount that would cancel the LIVE mount's older-page load. Set in the
   * effect body, cleared in its cleanup, so StrictMode's double run ends true.
   */
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * `writeRefusal`, for this hook's state AND the caller's session. Through a
   * ref so a retired mount's failure still reaches the session the live mount
   * shows.
   */
  const onWriteRefusalRef = useRef(controlled?.onWriteRefusal);
  onWriteRefusalRef.current = controlled?.onWriteRefusal;
  const onRefusedMentionIdsRef = useRef(controlled?.onRefusedMentionIds);
  onRefusedMentionIdsRef.current = controlled?.onRefusedMentionIds;
  // #1599 Story 1.4: the refused mention ids travel with the refusal. Every
  // `setWriteRefusal(null)` at the start of a write clears them too.
  const setWriteRefusal = useCallback(
    (
      refusal: string | null,
      refusedMentionIds: readonly string[] = NO_REFUSED_IDS,
    ) => {
      setState(s =>
        s.writeRefusal === refusal && s.refusedMentionIds === refusedMentionIds
          ? s
          : { ...s, writeRefusal: refusal, refusedMentionIds },
      );
      onWriteRefusalRef.current?.(refusal);
      onRefusedMentionIdsRef.current?.(refusedMentionIds);
    },
    [],
  );

  /**
   * Which message load is the current one.
   *
   * ⚠️ WHAT IT GUARDS CHANGED IN STORY 6.4, AND THE OLD REASON IS NOW
   * HISTORY. It used to be what kept a slow FIRST LOAD from landing on top of
   * a message the user had just posted: the open-thread effect and
   * `postMessage`'s re-fetch were two unrelated writers of one `useState`, and
   * the loser could be the one that started first. The library owns that race
   * now -- the post's re-fetch passes `cancelRefetch: true`, which is its own
   * "the last-started load wins", and a held first load is cancelled rather
   * than allowed to resolve into the cache.
   *
   * What it still guards is the pair the library does NOT arbitrate: a
   * `refreshOpenThread` fold, an `editMessage` map or a `deleteMessage`
   * tombstone that resolves AFTER a post's re-walk started must not land on
   * top of it. Those three observe the generation; only the post bumps it.
   *
   * ⚠️ SINCE STORY 6.5 THE OBSERVATION IS A MUTATION CONTEXT. The edit and the
   * delete capture the generation in `onMutate` -- which the library awaits
   * before it calls the mutation function (`mutation.js:96-107`), so it is the
   * same "before the call" moment the hand-rolled version captured it at -- and
   * compare it in `onSuccess`. The post still bumps it in its own `onSuccess`,
   * after the append and before the re-walk.
   */
  const loadGeneration = useRef(0);

  // ⚠️ THERE IS DELIBERATELY NO `memberKey` HERE ANY MORE (#1069 Story 6.3).
  // It was a value-stable string of the space's member ids, in the old dedupe
  // key and in the reload callback's dependency list, so a team change
  // re-listed. The listing's answer does not depend on the client's copy of
  // the membership — the server projects membership inside `listThreads` and
  // ignores anything a client sends — and nothing pinned it. It was in that
  // key because the callback depended on it, and the callback depended on it
  // because a reconciler that has since been deleted read it.
  //
  // The lesson it carried is NOT deleted; it moved to
  // `correspondenceQueries.ts`'s header, where the key now lives: `members` is
  // rebuilt on every render, so depending on the ARRAY once made the reload
  // callback a new function each time, which re-fired its effect, which set
  // state, which re-rendered — ~2,600 requests to the vendor in seconds.
  // Depend on contents, never on identity.
  //
  // `members` is still a parameter: `memberIdsForSpace` is gone from this
  // module but the argument is part of the hook's signature and the panel
  // passes it.

  /**
   * The viewer's id is in the thread query's KEY and in the message effect's
   * dependency list (Story 2.3, C6): `unread` on a row and the read row a page
   * marks are per person, so a swapped identity (impersonation start or stop
   * swaps `useAuth().user`) must re-list under the new person's read state and
   * re-page the open conversation.
   *
   * ⚠️ IT IS KEYED ON EXPLICITLY, AND STORY 6.3 NEARLY LOST IT. The epic's
   * proposed key was `[..., requestId, space]` with no viewer, which would
   * have shown one person's read state to another with no test to notice — a
   * query whose key does not change does not refetch on an argument change.
   * The reason it must be explicit is the original one: the member list does
   * not reliably change on an identity swap (it is the request team plus the
   * requestor since 2026-10-06, and before that `withViewer` only added a
   * viewer who was absent), so membership could never have stood in for it.
   */
  const viewerId = viewer?.id ?? null;

  // --- threads for the active space ----------------------------------------
  /**
   * The listing, as a React Query query (#1069 Story 6.3, NFR62).
   *
   * ⚠️ `enabled` GATES IT, AND THAT MATTERS BECAUSE THE OVERLAY PANEL IS NEVER
   * UNMOUNTED (#1069 P3.3). `CorrespondenceLauncher` renders
   * `<CorrespondencePanel visible={open}>` unconditionally, so closing the
   * panel only flips this flag — the hook and its rows survive. `staleTime: 0`
   * means re-enabling refetches (the observer remounts its subscription and
   * the data is stale by definition), which is what stops the panel reopening
   * on rows that may be minutes old. That was survivable while those rows only
   * fed a list; it is not now that the tab badge treats them as AUTHORITATIVE.
   * (The docked panel was always safe: `CorrespondenceDock` really does
   * unmount it.) Pinned by the re-enable test.
   *
   * No viewer, no listing: `unread` is per person, so a listing made before
   * the session user is known would be somebody's read state, not theirs.
   */
  const threadsState = useQuery({
    ...threadsQuery({ provider, requestId, space, viewerId }),
    enabled: enabled && viewerId !== null,
    /**
     * ⚠️ ROWS SURVIVE A TAB PRESS AND NOTHING ELSE (Story 6.3, D3).
     *
     * Two requirements pull apart here and the conditional shape is what
     * satisfies both. Blanking the list on every tab press is what the old
     * "never clear `threads`" rule existed to avoid, so the previous SPACE's
     * rows stay on screen while the new space loads — flagged
     * `isPlaceholderData`, so `threadsKey` below is `null` and the badge falls
     * back to the server. But a REQUEST change, or an identity swap, must not
     * inherit: showing request 1037's conversations under request 2048's
     * header, or one person's read state to another, is not a stale label but
     * a wrong list. Returning `undefined` there empties the rows and shows the
     * spinner instead.
     *
     * The house precedent for a conditional placeholder is
     * `useRequestsQuery.ts:63-88`. `keepPreviousData` (the v5 sentinel) cannot
     * express this: it never sees the key it would be inheriting across.
     * Segments 2 and 4 are `requestId` and `viewerId`
     * (`correspondenceThreadsKeys.list`).
     */
    placeholderData: (previous, previousQuery) => {
      if (!previousQuery) return undefined;
      /**
       * ⚠️ COMPARED AGAINST THE FACTORY, NOT AT INDEX `[2]` AND `[4]`
       * (code review, 2026-09-17). Those literals were correct and unpinned:
       * `correspondenceQueries.test.ts` asserts the key has five segments, but
       * nothing tied these two numbers to it, and `queryKey` is
       * `readonly unknown[]` so `tsc` could not object. Add a segment to
       * `list()` -- a cursor, a filter -- and index 4 silently becomes `space`:
       * the viewer comparison would quietly become a space comparison and an
       * impersonation swap would inherit the previous identity's rows as
       * placeholder data, with no test failing anywhere.
       *
       * Asking the factory what a key for THIS request and THIS viewer looks
       * like removes the arithmetic. `space` is deliberately excluded from the
       * comparison: inheriting across a tab press is the whole point (D3), so
       * only the segments that must NOT be inherited are compared.
       */
      return listKeyInheritableAcrossSpace(
        previousQuery.queryKey,
        requestId,
        viewerId,
      )
        ? previous
        : undefined;
    },
  });

  const queryClient = useQueryClient();

  /**
   * ⚠️ AN EXPLICIT ASK IS IN FLIGHT — AND THIS IS THE ONE PIECE OF LIST STATE
   * THE MIGRATION COULD NOT DERIVE. Measured 2026-09-16, not assumed.
   *
   * The contract `threadsKey` has carried since P3.3 is "null THE MOMENT a
   * load starts", and the panel's open-thread refresh gate is built on the
   * TRANSITION, not the value: it re-runs when `threadsKey`/`loadingThreads`
   * change, sees the key restored, and compares the open row's clock. Two
   * `setState` calls guaranteed two renders, so every listing produced that
   * transition.
   *
   * `useQuery` does not. React Query publishes observer updates through
   * `notifyManager.schedule` → `setTimeout(0)`, and the queued callback reads
   * the observer's CURRENT result when the timer fires rather than the result
   * at the moment it was queued. A listing that settles before that macrotask
   * — a stub, a cached response, a fast route — collapses `fetching` and
   * `success` into ONE render, and with structural sharing returning an
   * identical rows array it can collapse into NO render at all. Probed
   * directly: a `reloadThreads()` over unchanged data rendered zero times, and
   * over CHANGED data rendered once, settled, with `threadsKey` never null.
   * `notifyOnChangeProps: 'all'` does not change it; the collapse is in when
   * the callback reads, not in what it is allowed to report.
   *
   * The consequence is not cosmetic: the gate never fires, so a reply to the
   * conversation on screen never arrives without a press — which is the exact
   * failure the Story 2.3 review paid to fix.
   *
   * So an explicit ask flips this flag synchronously, before it awaits, and
   * clears it when the listing has landed. That is four lines of state whose
   * whole job is to make a transition observable that the library is entitled
   * not to publish. It is deliberately NOT a re-implementation of the
   * coalescer: it remembers nothing, decides nothing, and dedupes nothing —
   * the library still owns whether a fetch happens, what it returns and who
   * joins it.
   *
   * Mount and key changes need no such help: `threadsKey` is built from
   * `requestId` and `space`, so a tab press or a request change moves the
   * value itself, and a first load moves it from `null`.
   */
  /**
   * ⚠️ A COUNTER PER KEY, NOT ONE BOOLEAN (code review, 2026-09-17). It
   * shipped as `useState(false)`, and a boolean is wrong in two ways that both
   * publish a "settled" render while a listing is still coming:
   *
   * - **It cannot count.** Ask A (nothing in flight, one invalidation) and ask
   *   B arriving during A's flight (`wasFetching`, so two invalidations) share
   *   one flag. A's `finally` clears it while B's follow-up has not yet flipped
   *   `fetchStatus` to `fetching` -- invalidate, refetch and notify are three
   *   macrotasks -- so in that window `!isFetching && !listingAsked &&
   *   isSuccess && !isPlaceholderData` is TRUE over rows the follow-up is about
   *   to replace. `CorrespondencePanel.tsx:1124-1145` then pushes them up to
   *   the launcher pill as authoritative, which is the "badge appears and then
   *   corrects itself" class the panel's own comment exists to prevent.
   * - **It cannot tell one list from another.** A reload of the space you just
   *   left kept the flag raised over the space you moved to, so a list that was
   *   loaded and correct reported `loadingThreads` and a null `threadsKey`.
   *
   * Counting per key fixes both with the same structure, and the map is keyed
   * by the query key itself so it cannot drift from what is being asked for.
   * It is still NOT a re-implementation of the coalescer: it remembers no
   * results, decides no fetches and dedupes nothing. Its whole job remains
   * making a transition observable that the library is entitled not to publish
   * (see the note below on `notifyManager`).
   */
  const [asksByKey, setAsksByKey] = useState<ReadonlyMap<string, number>>(
    () => new Map<string, number>(),
  );

  const threadsKey_ = useMemo(
    () => correspondenceThreadsKeys.list(requestId, space, viewerId),
    [requestId, space, viewerId],
  );

  /** The active query key as one string, so asks can be counted per key. */
  const askKey = useMemo(() => threadsKey_.join('\u0000'), [threadsKey_]);

  const bumpAsk = useCallback((key: string, by: 1 | -1) => {
    setAsksByKey(prev => {
      const next = new Map(prev);
      const count = (next.get(key) ?? 0) + by;
      if (count > 0) next.set(key, count);
      else next.delete(key);
      return next;
    });
  }, []);

  /** Whether an explicit ask is in flight FOR THE LIST ON SCREEN. */
  const listingAsked = (asksByKey.get(askKey) ?? 0) > 0;

  /** Asks that arrived before a viewer existed. See `reloadThreads`. */
  const waitingForViewerRef = useRef<Array<() => void>>([]);

  /**
   * ⚠️ `[]` ON FAILURE, NOT THE LAST GOOD ROWS. `useQuery` keeps `data`
   * through a failed refetch, which is the right default nearly everywhere and
   * the wrong one here: the panel must not go on rendering conversations the
   * server has just refused to confirm, and `activeSpaceHasUnread` reads these
   * rows as an answer. Emptying is the safe direction and is what the hook did
   * before the migration.
   */
  /**
   * ⚠️ EXCEPT WHILE THE DEVICE IS OFFLINE (#1069 offline state). A listing
   * that failed for want of a connection is not the server refusing
   * anything, and emptying on it wiped a space the reader had already loaded
   * the moment they pressed its tab (the refetch on the key change fails).
   * So offline, a failed listing keeps THIS key's own last rows, and a
   * placeholder is refused: those rows are ANOTHER space's (D3's inheritance),
   * which online is a stale label for a moment and offline would stand in for
   * the real answer indefinitely. A space never loaded is empty, and the panel
   * shows its offline empty state there.
   */
  const threads = !deviceOnline
    ? threadsState.isPlaceholderData
      ? EMPTY_THREADS
      : threadsState.data ?? EMPTY_THREADS
    : threadsState.isError
    ? EMPTY_THREADS
    : threadsState.data ?? EMPTY_THREADS;

  /**
   * ⚠️ NULL WHILE ANY LISTING RUNS, NULL AFTER A FAILURE, THE KEY ONLY WHEN
   * THE ROWS ON SCREEN ARE THIS REQUEST'S AND THIS SPACE'S. Those are the
   * three states the hand-written field had, reproduced from the query's own
   * flags instead of from three writes:
   *
   * - `!isFetching` — the field went null the MOMENT a load started, for every
   *   load and not just a failing one, because that is the window in which the
   *   rows describe the previous space. Nothing about that changed.
   * - `isSuccess` — the failure branch.
   * - `!isPlaceholderData` — the structural version of the thing the field was
   *   invented to say. Rows inherited from another space's query are somebody
   *   else's answer BY CONSTRUCTION, and now the label cannot disagree with
   *   the rows, because both are read from the same query state.
   */
  const threadsKey =
    !threadsState.isFetching &&
    !listingAsked &&
    threadsState.isSuccess &&
    !threadsState.isPlaceholderData
      ? threadsKeyFor(requestId, space)
      : null;

  /**
   * ⚠️ `isFetching`, NOT `isLoading`. `isLoading` is the FIRST load only; the
   * panel's open-thread refresh gate asks "is a listing in flight" and a
   * reload has to answer yes. The spinner is separately gated on an empty
   * list, so a reload over existing rows shows nothing either way.
   */
  const loadingThreads = threadsState.isFetching || listingAsked;

  /**
   * ⚠️ ONE `error` SLOT, SHARED WITH EVERY WRITER, LAST WRITER WINS. The
   * query owns its own error; the hook owns the writers'. A write error takes
   * precedence while it is set, and the effect below takes it down when the
   * next listing starts.
   *
   * The message is built through `describe()` like every other catch in this
   * file — the lazy-Hermes guard is about where a catch parameter is READ, and
   * a query error is no different once it reaches a closure.
   */
  /**
   * ⚠️ AND A QUERY ERROR IS SUPPRESSED WHILE A LISTING IS IN FLIGHT (code
   * review, 2026-09-17). `threadsState.error` belongs to the CACHED query and
   * survives until that query next succeeds, which reintroduced a banner the
   * migration was supposed to have kept impossible: a failed `internal`
   * listing, a press of `shared`, a press back to `internal`, and React Query
   * re-serves the errored entry -- so the old failure text reappeared while the
   * refetch was already running, then vanished when it succeeded.
   *
   * Before the migration a listing START wrote `error: null`, so a retry in
   * progress never showed the previous attempt's message. `!isFetching`
   * restores exactly that: the banner speaks when an attempt has finished and
   * failed, never while the next one is on the wire. The write-error slot is
   * unaffected -- it is cleared by the effect below, on the same trigger it
   * always was.
   */
  const threadsError = threadsState.isFetching ? null : threadsState.error;

  /**
   * ⚠️ THE ONE PIECE OF HAND-ROLLED LIST STATE THAT SURVIVES, AND IT IS ABOUT
   * THE BANNER RATHER THAN THE LIST. Before the migration a listing START
   * wrote `error: null`, which cleared ANY error including a refused post — so
   * the banner came down on the next clock tick rather than living forever.
   * `useQuery` clears nothing on a fetch start, so that behaviour has to be
   * reproduced, and it is user-visible enough to have two panel suites and a
   * hook pin on it.
   */
  useEffect(() => {
    if (!threadsState.isFetching && !listingAsked) return;
    setState(s => (s.error === null ? s : { ...s, error: null }));
  }, [threadsState.isFetching, listingAsked]);

  /**
   * Ask for a fresh listing.
   *
   * ⚠️ A CALL THAT ARRIVES WHILE ONE IS IN FLIGHT IS NOT DROPPED, IT IS
   * COALESCED (code review of Story 2.3, 2026-09-11, the HIGH finding). The
   * clock path relies on this: the summary tick says "the audience moved", and
   * a listing already on the wire was issued BEFORE that move, so its answer
   * cannot carry it. Dropping the second call left the reply invisible until
   * the reader pressed a tab, with no error anywhere (reproduced under a gated
   * stub). N calls during one flight cost ONE extra listing, never N and never
   * zero, and the follow-up carries whatever was true when IT ran.
   *
   * ⚠️ THE LIBRARY'S DEFAULT DOES NEITHER HALF OF THAT, MEASURED AGAINST
   * `@tanstack/query-core@5.96.2` rather than assumed (Story 6.3, C3):
   * - `invalidateQueries` defaults to `cancelRefetch: true`, so over existing
   *   data each ask silently CANCELS the flight and starts another. Three asks
   *   during one flight are three extra listings; and the transport takes no
   *   `AbortSignal`, so every cancelled fetch is still a real request against
   *   a rate-limited route.
   * - With no data yet, the ask is deduped onto the in-flight promise and the
   *   `invalidate` is then erased by the `success` reducer — the move is
   *   DROPPED, which is the 2026-09-11 finding exactly.
   *
   * So: join the flight (`cancelRefetch: false`), and if there WAS a flight at
   * the moment of the ask, issue one follow-up after it settles. Every ask
   * that arrives during one flight joins the same follow-up, because that
   * second invalidation dedupes the same way. `fetchStatus` is read from the
   * library; nothing here remembers anything.
   *
   * ⚠️ AND IT NOW RESOLVES WHEN THE LISTING HAS LANDED (Story 6.3, C12). It
   * used to resolve AT ONCE for a coalesced ask, having only requested the
   * follow-up, which is why `createThread` could return an id whose row was
   * not in the list yet and the header read "Discussion" until the follow-up
   * arrived. `invalidateQueries` resolves after the refetch settles, so that
   * race is gone. Callers that await this get what they were always written
   * to expect.
   */
  const reloadThreads = useCallback(async () => {
    if (!enabled) return;
    /**
     * ⚠️ NO VIEWER YET IS NOT A REFUSAL, IT IS A WAIT (code review,
     * 2026-09-17, D1). This used to be `if (!enabled || !viewerId) return`, so
     * an ask arriving while the session user was unknown resolved having done
     * nothing and told no one. `viewer` IS briefly null on an impersonation
     * start or stop, and `createThread` awaits this call before it returns an
     * id -- so the panel could open a conversation whose row was not in the
     * list and the header fell back to "Discussion". That is exactly the race
     * C12 says awaiting `invalidateQueries` removed; the early return had
     * reopened it in a narrower window.
     *
     * The ask is parked instead, and the effect below issues it the moment a
     * viewer exists. Callers that await this keep the guarantee they were
     * written to expect: when it resolves, the listing has landed.
     *
     * The wait is bounded by the hook's own life -- the unmount cleanup below
     * releases every parked ask -- so this cannot strand a caller forever. It
     * is deliberately NOT bounded by a timer: a timeout would resolve with the
     * listing still missing, which is the behaviour being fixed.
     */
    if (!viewerId) {
      await new Promise<void>(resolve => {
        waitingForViewerRef.current.push(resolve);
      });
      return;
    }
    // Captured, because the counter must be decremented on the key it was
    // incremented on even if the request, space or viewer moves mid-flight.
    const key = askKey;
    const wasFetching =
      queryClient.getQueryState(threadsKey_)?.fetchStatus === 'fetching';
    // Synchronously, BEFORE the await: see `listingAsked`.
    bumpAsk(key, 1);
    try {
      await queryClient.invalidateQueries(
        { queryKey: threadsKey_ },
        { cancelRefetch: false },
      );
      if (wasFetching) {
        await queryClient.invalidateQueries(
          { queryKey: threadsKey_ },
          { cancelRefetch: false },
        );
      }
    } finally {
      bumpAsk(key, -1);
    }
  }, [enabled, viewerId, queryClient, threadsKey_, askKey, bumpAsk]);

  /**
   * Issue the asks that arrived before the viewer did, and release whoever is
   * awaiting them. Resolution is deliberately tied to the listing that follows
   * rather than to the viewer merely appearing, so `await reloadThreads()`
   * means the same thing on both paths. A failed listing releases the waiters
   * too: the banner reports it, and leaving a caller hanging on a refusal
   * would be worse than telling it the attempt is over.
   */
  useEffect(() => {
    if (!enabled || !viewerId) return;
    if (waitingForViewerRef.current.length === 0) return;
    const waiting = waitingForViewerRef.current;
    waitingForViewerRef.current = [];
    const release = () => waiting.forEach(resolve => resolve());
    void reloadThreads().then(release, release);
  }, [enabled, viewerId, reloadThreads]);

  /** No parked ask outlives the hook. See `reloadThreads`. */
  useEffect(
    () => () => {
      const waiting = waitingForViewerRef.current;
      waitingForViewerRef.current = [];
      waiting.forEach(resolve => resolve());
    },
    [],
  );

  // --- messages for the open thread ----------------------------------------
  /**
   * The open conversation's pages, as a React Query infinite query (#1069
   * Story 6.4, NFR62). Replaces a `useState` slot, one effect, a cursor ref,
   * an in-flight ref keyed by thread and a merge closure over `State`.
   *
   * ⚠️ `enabled` IS THE OPEN THREAD AND THE VIEWER, DELIBERATELY NOT THE
   * HOOK'S `enabled` ARGUMENT. The overlay panel is never unmounted, so that
   * flag goes false whenever the panel is closed -- and the thread-list query
   * uses it for exactly that reason. The open conversation must NOT be
   * re-paged by a panel re-enable: "no re-page on re-enable" is a pin, and
   * today's effect is keyed `[openThreadId, provider, viewerId]` with no
   * `enabled` in sight. No viewer, no pages: visibility is per person.
   */
  const messagesKey_ = useMemo(
    () => correspondenceMessagesKeys.thread(openThreadId, viewerId),
    [openThreadId, viewerId],
  );

  const messagesState = useInfiniteQuery({
    ...messagesQuery({ provider, threadId: openThreadId, viewerId }),
    enabled: openThreadId !== null && viewerId !== null,
    /**
     * ⚠️ NOT ON MOUNT (review, 2026-09-26). The open conversation lives in
     * the session, so a resize or a docked reopen MOUNTS this observer onto a
     * thread whose pages are already cached, and `staleTime: 0` re-walked
     * every loaded page, one request each, per crossing. What a remount owes
     * the reader is anything NEW, and the panel's clock step already fetches
     * the newest page when the listing (which does refetch on mount) shows the
     * open row moved. With no cached data this still fetches: the option only
     * applies to a query that has data. A thread SWITCH is a key change, not
     * a mount, and is unaffected.
     */
    refetchOnMount: false,
  });

  const { fetchNextPage } = messagesState;

  /**
   * The open conversation's file listing (#1069 Story 7.4, FR77).
   *
   * ⚠️ `enabled` IS THE MESSAGE PAGES' CONDITION, NOT THE STRIP'S OPEN STATE.
   * The owner decided (2026-09-17) that the count comes from the listing
   * itself — "Files (n)" cannot know `n` without the list, and a head request
   * was ruled out — so the data is fetched when a conversation opens and only
   * the strip's OPEN state is lazy (C1, D1). The cost is one listing per
   * conversation open for a reader who never opens the strip; it is bounded by
   * the server's ceiling and paid once, like the newest page.
   */
  const threadFilesKey_ = useMemo(
    () => correspondenceThreadFilesKeys.thread(openThreadId, viewerId),
    [openThreadId, viewerId],
  );

  const threadFilesState = useQuery({
    ...threadFilesQuery({ provider, threadId: openThreadId, viewerId }),
    enabled: openThreadId !== null && viewerId !== null,
  });

  /**
   * ⚠️ `null` ON ERROR AS WELL AS WHILE LOADING, and that is the whole of D8.
   * A failed listing is SILENT: the row is absent, not present and broken,
   * because the bubbles below it still show every one of these files and a
   * banner reading "Discussion error" over a working conversation would say
   * more than went wrong. The next invalidation or the next open retries it.
   * Recorded for the owner in `deferred-work.md`.
   *
   * ⚠️ `isError` IS READ, NOT JUST `data` (code review, 2026-09-18). React
   * Query PRESERVES `state.data` across a failed refetch, so `data ?? null`
   * is "null on error" only until the first success — after it, a reader
   * removed from the request team kept seeing the count and every file name
   * in the strip, and a withdraw whose refetch was refused kept the withdrawn
   * drawing. The docblock above was true of the first fetch only.
   */
  const threadFiles = threadFilesState.isError
    ? null
    : threadFilesState.data ?? null;

  /**
   * Fire-and-forget, wrapped exactly as the post's refetch is. Called at the
   * three sites D6 names and NOWHERE else.
   *
   * ⚠️ INVALIDATED, NEVER PATCHED. The message pages have four `patch(` sites
   * and this file's header says a fifth `setQueryData` is a review finding;
   * the files listing joins the author's name and the message's time in SQL,
   * so a hand-built optimistic row would be this client inventing two fields
   * the server owns. With `staleTime: 0` and the observer mounted while a
   * conversation is open, an invalidation IS the refetch.
   *
   * ⚠️ `viewerIdRef.current`, NOT THE RENDER-SCOPE `viewerId` (code review,
   * 2026-09-18). Two of the three call sites sit one line under a `patch`
   * keyed on `viewerIdRef.current`, and those writers' callbacks are captured
   * on the `Mutation` when `mutate()` is called — so a post in flight across
   * an impersonation swap patched the new viewer's entry and invalidated the
   * old one's. Same rule, same reason, as `viewerIdRef`'s own note below.
   */
  const invalidateThreadFiles = useCallback(
    (threadId: string) => {
      void queryClient
        .invalidateQueries({
          queryKey: correspondenceThreadFilesKeys.thread(
            threadId,
            viewerIdRef.current,
          ),
          exact: true,
        })
        .catch(() => {});
    },
    [queryClient],
  );

  const reloadThreadFiles = useCallback(async (): Promise<void> => {
    const threadId = openThreadIdRef.current;
    if (!threadId) return;
    await queryClient
      .invalidateQueries({
        queryKey: correspondenceThreadFilesKeys.thread(
          threadId,
          viewerIdRef.current,
        ),
        exact: true,
      })
      .catch(() => {});
  }, [queryClient]);

  /**
   * The cache's own shape. Spreading `messagesQuery(...)` into
   * `useInfiniteQuery` widens the page-param type to `unknown`, because the
   * spread loses the factory's `initialPageParam` annotation; the factory is
   * the declaration of what a page param IS (`provider.types.ts`'s opaque
   * cursor), so this narrows back to it rather than weakening `MessagePages`.
   */
  const messagesData = useMemo(
    () => messagesState.data as MessagePages | undefined,
    [messagesState.data],
  );

  /**
   * The messages query's error, readable from the listing-start effect without
   * putting it in that effect's dependency list -- the effect fires on the
   * listing's transition and must read whatever was showing AT THAT MOMENT,
   * not re-run when a new failure arrives.
   */
  const messagesStateErrorRef = useRef<unknown>(null);
  /**
   * ⚠️ ASSIGNED IN AN EFFECT, NOT DURING RENDER, like `openThreadIdRef` and
   * `viewerIdRef` (code review, 2026-09-17). It was written in the render body,
   * which React may discard; the suppression effect above reads it on commit,
   * so an abandoned render could have handed it an error that never reached the
   * screen. Harmless in practice and inconsistent with the rule the rest of
   * this file states twice, which is reason enough to match it.
   */
  useEffect(() => {
    messagesStateErrorRef.current = messagesState.error;
  }, [messagesState.error]);

  /**
   * The viewer's id, readable from a callback that has already awaited.
   *
   * ⚠️ THE WRITERS' CALLBACKS ARE `[provider]`-STABLE AND `provider` IS
   * `useMemo(..., [])`, so they are built ONCE. A closure over the `viewerId`
   * VALUE would therefore be the first render's forever, and every cache key
   * they built after an impersonation swap would name the wrong person -- the
   * patch would land on an entry nothing is observing and the write would
   * silently not appear. Same reason, and the same effect-assignment rule, as
   * `openThreadIdRef`.
   */
  const viewerIdRef = useRef<string | null>(null);
  useEffect(() => {
    viewerIdRef.current = viewerId;
  }, [viewerId]);

  /**
   * ⚠️ APPLIED NOW, AND AGAIN AFTER WHATEVER WAS IN FLIGHT LANDS (Story 6.4,
   * C6, D2). React Query's infinite behaviour snapshots `pages` when a fetch
   * STARTS (`infiniteQueryBehavior.js:10-13`) and writes snapshot-plus-page
   * when it settles (:47-49), so a patch made while "Load earlier messages" is
   * on the wire is silently undone the moment that page lands. That is NOT a
   * hazard the hand-rolled code had -- two functional `setState` updaters
   * compose -- and an edit surviving a held older page is pinned.
   *
   * Every updater in `correspondenceQueries.ts` is idempotent (by message id,
   * or by page boundary), so re-applying one is free when nothing clobbered it
   * and exact when something did. A fetch that starts AFTER the first
   * application snapshots the patched pages and needs nothing.
   *
   * ⚠️ IT IS NOT A COALESCER. It remembers nothing and decides nothing: it
   * reads `fetchStatus` once and re-applies a pure function after a promise
   * the library itself exposes. The alternative -- await the in-flight fetch
   * BEFORE patching -- would delay `editMessage`'s resolution until an older
   * page lands, and the edited body is read synchronously after that await.
   */
  const patch = useCallback(
    (
      key: MessagesKey,
      updater: (old: MessagePages | undefined) => MessagePages | undefined,
    ) => {
      queryClient.setQueryData<MessagePages>(key, updater);
      const query = queryClient
        .getQueryCache()
        .find<MessagePages>({ queryKey: key, exact: true });
      if (query?.state.fetchStatus !== 'fetching') return;
      const settled = query.promise;
      if (!settled) return;
      /**
       * ⚠️ ONLY IF THE ENTRY IS STILL THERE (code review, 2026-09-17).
       * `appendMessage` and `foldNewestPage` MINT on `undefined` -- deliberately,
       * so a post that beats the first page is visible at once. But this
       * re-apply is asynchronous, and the thread-switch effect's
       * `removeQueries` both deletes the entry AND rejects the fetch this is
       * waiting on, so the rejection path would re-mint the key the switch just
       * removed: a one-message conversation under the thread the user LEFT,
       * with no cursor and so no "Load earlier" control on reopen. That breaks
       * D3's pin that a thread switch clears the per-thread state.
       * `mapMessages` is `undefined`-in/`undefined`-out and was never at risk;
       * the two minting updaters are, which is why the guard is here rather
       * than in them.
       */
      const reapply = () => {
        const live = queryClient
          .getQueryCache()
          .find<MessagePages>({ queryKey: key, exact: true });
        if (!live) return;
        queryClient.setQueryData<MessagePages>(key, updater);
      };
      void settled.then(reapply, reapply);
    },
    [queryClient],
  );

  /**
   * A write that settled for a conversation the user LEFT WHILE OFFLINE
   * (`keptOffline.ts`) patches that conversation's kept pages, so a return
   * while still offline shows the user's own delivered change. Only a kept
   * entry that still exists: never mints one (a removed entry is refetched on
   * return), and the kept entry's loads were cancelled when it was kept, so
   * `patch`'s re-apply after an in-flight fetch has nothing to wait for.
   */
  const patchKept = useCallback(
    (
      key: MessagesKey,
      updater: (old: MessagePages | undefined) => MessagePages | undefined,
    ) => {
      if (!isKeptOffline(queryClient, key)) return;
      if (queryClient.getQueryData<MessagePages>(key) === undefined) return;
      queryClient.setQueryData<MessagePages>(key, updater);
    },
    [queryClient],
  );

  /**
   * ⚠️ THE PREVIOUS THREAD'S ENTRY IS REMOVED, NOT LEFT TO GC (Story 6.4,
   * D3, C8). "A thread switch clears the per-thread state" has to stay true,
   * and `gcTime: 0` cannot do it: `Removable.updateGcTime` is a `Math.max`
   * over every setter (`removable.js:18-22`) and `setQueryData` builds a
   * missing query with the CLIENT's defaults (`queryClient.js:93-104`), so the
   * first cache patch raises the entry to 10 minutes in the app and to
   * `Infinity` under the test client. A reopened thread would then show its
   * stale pages instantly and re-walk every one of them.
   *
   * A removal also takes an in-flight older page for the thread the user LEFT
   * with it -- `query.destroy()` cancels silently, which is the test harness's
   * own documented disposal and is what the generation guard achieved by hand.
   *
   * ⚠️ IN THE EFFECT BODY, KEYED ON THE CHANGE, NOT IN A CLEANUP. React
   * StrictMode double-invokes effects in development; a cleanup would remove
   * the LIVE entry and trigger an extra fetch of the thread the user is
   * looking at. A `null` thread key is never removed -- nothing is under it.
   *
   * ⚠️ EXCEPT WHILE THE DEVICE IS OFFLINE (2026-09-26,
   * `spec-1069-offline-keeps-visited-threads.md`, owner report). Offline, the
   * reopen that D3 relies on to refill a conversation cannot succeed, so
   * removing the entry turned a conversation that WAS on screen into the
   * offline empty state after a tab round trip, a Back, or A -> B -> A. So a
   * leave while offline RECORDS the two keys instead (`keptOffline.ts`, held
   * on the query client so a request switch or an unmount cannot lose it),
   * and the reconnect effect below removes every recorded one except the open
   * conversation's, which puts D3's rule back the moment a refetch could
   * succeed. Online, nothing here changed. A REVOKE is never kept, online or
   * not: the reader has lost the conversation, so its pages go at once.
   */
  const localThreadCacheKeysRef = useRef<{
    messages: readonly unknown[];
    files: readonly unknown[];
  } | null>(null);
  /**
   * ⚠️ CONTROLLED, THE LAST KEYS LIVE IN THE SESSION (review, 2026-09-26).
   * A per-mount ref starts `null`, so a thread change that happened while NO
   * panel was mounted (a create landing after a docked close opens the new
   * conversation in the session) was never compared against the thread it
   * replaced, and that thread's pages stayed cached. Seeded from the session,
   * the first run of a new mount sees the change and removes them.
   */
  const threadCacheKeysRef =
    controlled?.threadCacheKeys ?? localThreadCacheKeysRef;
  const keptOfflineRef = useMemo(
    () => keptOfflineFor(queryClient),
    [queryClient],
  );
  const accessRevoked = controlled?.accessRevoked ?? false;
  useEffect(() => {
    const last = threadCacheKeysRef.current;
    const previous = last?.messages ?? null;
    // Both advanced together, ABOVE every early return, so the two keys
    // always describe the same render.
    const previousFilesForThisRun = last?.files ?? null;
    threadCacheKeysRef.current = {
      messages: messagesKey_,
      files: threadFilesKey_,
    };
    if (!previous) return;
    if (messagesKeyHasNoThread(previous)) return;
    if (previous.every((segment, i) => segment === messagesKey_[i])) return;
    if (!deviceOnline && !accessRevoked) {
      // Kept, not removed: see the offline exception above. Both keys, kept
      // and later removed TOGETHER (D9), so an online return never finds a
      // conversation's pages without its listing or the reverse. (Offline the
      // Files row itself stays hidden, per the offline-state spec: the files
      // entry is kept for that consistency, not to show the row.)
      // Deduplicated by hash, since A -> B -> A -> B offline leaves each more
      // than once.
      //
      // ⚠️ CANCELLED FIRST (review, 2026-09-26). The removal this replaces
      // also destroyed any load in flight for the left conversation; kept, an
      // older page requested before the drop would still land in it, or fail
      // into it, while nobody is looking. Cancelling reverts the entry to what
      // was on screen when the user left, which is exactly what is kept.
      for (const key of [previous, previousFilesForThisRun]) {
        if (key) void queryClient.cancelQueries({ queryKey: key, exact: true });
      }
      const kept = keptOfflineRef.current;
      const seen = new Set(kept.map(key => hashKey(key)));
      const add = [previous, previousFilesForThisRun].filter(
        (key): key is readonly unknown[] =>
          key !== null && !seen.has(hashKey(key)),
      );
      if (add.length > 0) keptOfflineRef.current = [...kept, ...add];
      return;
    }
    queryClient.removeQueries({ queryKey: previous, exact: true });
    // ⚠️ THE FILES GO WITH THE PAGES, IN THE SAME EFFECT (Story 7.4, D9).
    // "A thread switch clears the per-thread state" is ONE rule (Story 6.4
    // D3/C8), and a files entry left behind would make a reopened
    // conversation show the PREVIOUS conversation's count for as long as its
    // refetch takes.
    //
    // ⚠️ THE PREVIOUS FILES KEY IS REMEMBERED, NOT DUG OUT OF `previous` BY
    // INDEX. The comment on `THREAD_SEGMENT` in `correspondenceQueries.ts`
    // records what index arithmetic over a `readonly unknown[]` costs: `tsc`
    // has nothing to say about it, and one added segment inverts the guard.
    // Two refs advanced in one effect cannot address different threads.
    if (previousFilesForThisRun) {
      queryClient.removeQueries({
        queryKey: previousFilesForThisRun,
        exact: true,
      });
    }
  }, [
    messagesKey_,
    threadFilesKey_,
    queryClient,
    threadCacheKeysRef,
    keptOfflineRef,
    deviceOnline,
    accessRevoked,
  ]);

  /**
   * ⚠️ A REVOKE TAKES EVERY KEPT CONVERSATION WITH IT, OFFLINE TOO (review,
   * 2026-09-26). Kept entries name a thread and a viewer, never a request, so
   * which of them belong to the revoked request cannot be told apart; all of
   * them go, and the cost is only that another request's conversation, if one
   * was kept, refetches instead of showing offline. The open conversation's
   * own entries go through the removal effect above, which a revoke reaches
   * by clearing the session's thread.
   */
  useEffect(() => {
    if (!accessRevoked) return;
    const kept = keptOfflineRef.current;
    if (kept.length === 0) return;
    keptOfflineRef.current = [];
    for (const key of kept) {
      queryClient.removeQueries({ queryKey: key, exact: true });
    }
  }, [accessRevoked, keptOfflineRef, queryClient]);

  /**
   * The other half of the offline exception: once the device is online, every
   * conversation kept while offline goes, except the one open now (the
   * panel's reconnect refreshes that one in place). Declared AFTER the removal
   * effect and inside the hook, so it runs before the panel's reconnect
   * reloads in the same commit: nothing can attach to a kept entry first.
   *
   * ⚠️ NOT AN EDGE DETECTOR. It runs whenever the device is online and the
   * record is not empty, so a reconnect that happened while NO panel was
   * mounted (offline, docked close, back online, reopen) is settled by the
   * next mount's first run, the way `reconnectPending` is. A flap clears the
   * record on the online half; a leave in the next offline period records
   * afresh.
   */
  useEffect(() => {
    if (!deviceOnline) return;
    const kept = keptOfflineRef.current;
    if (kept.length === 0) return;
    keptOfflineRef.current = [];
    const openPagesId = hashKey(messagesKey_);
    const openFilesId = hashKey(threadFilesKey_);
    for (const key of kept) {
      const keyId = hashKey(key);
      if (keyId === openPagesId) {
        /**
         * ⚠️ THE OPEN ONE IS TRIMMED TO ITS NEWEST PAGE, NOT KEPT WHOLE
         * (review, 2026-09-26). The panel's reconnect refreshes only the
         * newest page, so older pages loaded before the drop would stay on
         * screen as if current, with an edit or a withdrawal made while the
         * user was away invisible. Trimmed, they come back through Load
         * earlier, fetched now. Not removed: that would empty a conversation
         * the user is looking at, and refetch every page.
         */
        queryClient.setQueryData<MessagePages>(key, data =>
          data && data.pages.length > 1
            ? {
                pages: data.pages.slice(0, 1),
                pageParams: data.pageParams.slice(0, 1),
              }
            : data,
        );
        continue;
      }
      if (keyId === openFilesId) continue;
      queryClient.removeQueries({ queryKey: key, exact: true });
    }
  }, [
    deviceOnline,
    messagesKey_,
    threadFilesKey_,
    queryClient,
    keptOfflineRef,
  ]);

  /**
   * What the open-thread effect's START WRITE did, minus the load (Story 6.4,
   * D3) and minus the pending rows (Story 6.6, D4).
   *
   * ⚠️ IT NO LONGER CLEARS THE ROWS, AND NOTHING REPLACED THAT CLEAR. The
   * previous thread's rows used to die here so that thread A's row could not
   * sit under thread B's header. Since Story 6.6 they are not cleared because
   * they were never copied: `toPendingRows` keeps the rows whose `threadId` is
   * the open one, so A's row is simply not B's -- and it is still there on the
   * way back, which is the behaviour change that story is for.
   *
   * ⚠️ `error` IS CLEARED ON OPEN ONLY, WHICH IS WHAT THE REMOVED CODE DID
   * (code review, 2026-09-17). The old effect cleared `error` inside its
   * `openThreadId && viewerId` branch; its CLOSE branch cleared the messages,
   * the pending rows and the cursor and left `error` alone. An unbranched
   * clear here took a refused post's banner down when the user pressed Back to
   * the thread list -- a refusal the reader was still reading, gone with no
   * action of theirs and no record left (`writeRefusal` is separate state the
   * panel only prefers in the composer).
   */
  useEffect(() => {
    const opening = openThreadId !== null && viewerId !== null;
    if (!opening) return;
    setState(s => (s.error === null ? s : { ...s, error: null }));
  }, [openThreadId, viewerId]);

  /**
   * FR56: opening a thread clears its unread state (#1069 P3.1), fired when
   * the FIRST PAGE HAS LANDED (Story 6.4, D6).
   *
   * ⚠️ AFTER THE LOAD SUCCEEDS, AND FIRE AND FORGET. After, because marking
   * a thread read that failed to render would be claiming the user saw
   * something they did not -- and the failure direction of the other order
   * (the thread stays unread) is the harmless one. Fire-and-forget, because
   * read state is an affordance: a banner over a thread the user is reading is
   * worse than a stale badge, so this must never reach `state.error`.
   *
   * ⚠️ THE TOKEN IS WHAT MAKES "ONCE PER OPEN" TRUE UNDER HONEST
   * DEPENDENCIES. `isSuccess` is not "the first page landed": it flips
   * false -> true again after a failed-then-successful older page. And the
   * effect must carry `threadsKey_`, because the row clear below patches the
   * space this effect was ABOUT (C9) -- so a tab press re-runs it. Neither
   * re-marks. A -> B -> A does (the token moved twice), and an identity swap
   * marks for the new person, who is the one looking at it.
   *
   * ⚠️ A POST THAT RACED THE FIRST LOAD MINTS `data`, which makes
   * `isSuccess` true before any page landed, so mark-read fires one request
   * early in that window. Recorded rather than hidden: the server marks the
   * thread read for the author on post anyway, so the extra call changes no
   * state.
   */
  const localMarkedRef = useRef<string | null>(null);
  const markedRef = controlled?.markedToken ?? localMarkedRef;
  const firstPageLanded = messagesState.isSuccess;
  useEffect(() => {
    /**
     * ⚠️ THE TOKEN IS RELEASED WHEN THE THREAD CLOSES (code review,
     * 2026-09-17, FR56 regression). This effect used to early-return on a null
     * thread WITHOUT touching `markedRef`, so the token still said
     * `A\0viewer` when A was reopened and the mark was skipped. A -> B -> A
     * moved the token twice and was fine; A -> null -> A is the mainline Back
     * gesture (`CorrespondencePanel.tsx`'s `leaveOpenThread` is exactly
     * `setOpenThreadId(null)`) and was not. Measured: a probe asserting two
     * marks across open/Back/reopen passes on the pre-migration hook and fails
     * here. `markThreadReadCalls` now pins it.
     */
    if (!openThreadId || !viewerId) {
      markedRef.current = null;
      return;
    }
    /**
     * ⚠️ AND RELEASED WHILE THE PANEL IS CLOSED, AND NEVER MARKED THEN
     * (review, 2026-09-26). Since the token lives in the session it outlives
     * a docked close; kept through the close, a reopen onto a conversation
     * with new messages showed them and never marked them read, leaving the
     * launcher and tab dots lit over a thread the user was reading. And a
     * panel that is not showing marks nothing: a hidden overlay mounted by a
     * resize while closed must not mark a conversation read behind the user's
     * back. Released here, so the reopen marks once its page is on screen; a
     * resize while OPEN keeps `enabled` true and keeps the token.
     */
    if (!enabled) {
      markedRef.current = null;
      return;
    }
    if (!firstPageLanded) return;
    const token = `${openThreadId}\u0000${viewerId}`;
    if (markedRef.current === token) return;
    markedRef.current = token;
    // Captured now: a mark-read that resolves after a tab press must patch the
    // space this effect was about, not the one the user moved to.
    const keyAtEffectTime = threadsKey_;
    provider
      .markThreadRead(openThreadId)
      .then(() => {
        // ⚠️ ONLY ON SUCCESS. Telling the screen to refresh its dot after a
        // FAILED write would clear the badge for a read that was never
        // recorded, and the server would put it straight back on the next
        // load -- a flicker that looks like the dot is lying.
        onThreadReadRef.current?.();
        /**
         * ⚠️ AND CLEAR THE DOT ON THE ROW THE USER JUST OPENED. Review found
         * this missing, and it was the worst outcome this design can produce:
         * `onThreadRead` refreshes the SCREEN's queries, but the panel's own
         * rows are loaded once per `[requestId, space, viewerId]` and none of
         * those change on a mark-read -- so the launcher dot cleared and the
         * THREAD ROW stayed lit until the panel was reopened. The space-tab
         * dot inherited it too, because `tabUnread` UNIONS the local rows with
         * the server answer.
         *
         * ⚠️ THE KEY CAPTURED WHEN THIS EFFECT RAN, NOT THE CURRENT ONE, and
         * `rows?.map` with the `?` LOAD-BEARING: an updater that receives
         * `undefined` must RETURN `undefined`, or `setQueryData` mints a cache
         * entry out of nothing and a later observer reads a list no listing
         * produced.
         *
         * #1599 Story 1.5 (AC 7): the mention badge clears with the dot. The
         * thread read marker is its only input (AD-3), so the same successful
         * write that clears `unread` clears `mentionsViewer`; on a row that is
         * mentioned but not unread it still clears, hence the `||`. An
         * edit-added mention produces exactly that row (#1599 Story 1.8): an
         * edit writes a mention row at the edit instant without a new
         * message, so the `@` lights while the dot stays off. Keep the `||`
         * for that case. A row whose key is ABSENT (the server's
         * flag is off) stays without one: "not computed" is not "false".
         */
        queryClient.setQueryData<Thread[]>(keyAtEffectTime, rows =>
          rows?.map(t =>
            t.id === openThreadId && (t.unread || t.mentionsViewer)
              ? {
                  ...t,
                  unread: false,
                  ...(t.mentionsViewer === undefined
                    ? {}
                    : { mentionsViewer: false }),
                }
              : t,
          ),
        );
      })
      .catch(() => {
        // Deliberately swallowed. See above.
      });
  }, [
    openThreadId,
    viewerId,
    enabled,
    firstPageLanded,
    provider,
    queryClient,
    threadsKey_,
    // Stable for the hook's life whichever owns it (the session's `refFor`
    // or the local ref); named because the linter cannot see through either.
    markedRef,
  ]);

  /**
   * The open conversation, in reading order.
   *
   * `useMemo` over the query's RAW `data` rather than a `select`: the patch
   * helpers need the cache's own shape, and a `select` would make the
   * observer's `data` a different thing from the cache's.
   */
  const messages = useMemo(
    () =>
      messagesData ? messagesInReadingOrder(messagesData) : EMPTY_MESSAGES,
    [messagesData],
  );

  /**
   * ⚠️ GATED ON `hasNextPage`, WHICH IS THE LIBRARY'S OWN END SIGNAL
   * (`getNextPageParam(lastPage) != null`). The two are equal by construction,
   * and asking the library is what makes "an empty page WITH a cursor is not
   * the end" impossible to get wrong here. On a key change the observer
   * publishes an optimistic pending result synchronously, so this is `null`
   * immediately after a thread switch, which a pin reads right after `act`.
   */
  const olderMessagesCursor = messagesState.hasNextPage
    ? olderCursorOf(messagesData)
    : null;
  /**
   * `isLoading` is `isPending && isFetching`, so this is `false` with no
   * thread open (the query is disabled), `false` after a failed first load,
   * and `false` during a post's re-walk over rows already on screen. All three
   * are what the hand-written field did; only the open-thread effect set it.
   */
  const loadingMessages = messagesState.isLoading;
  /**
   * `isFetchingNextPage` is `isFetching && fetchMeta.fetchMore.direction ===
   * 'forward'` (`infiniteQueryObserver.js:49`) -- a post's re-walk is not
   * "older", which is the distinction the hand-written flag also made.
   */
  const loadingOlder = messagesState.isFetchingNextPage;

  /**
   * ⚠️ A LISTING START STILL TAKES A MESSAGES ERROR DOWN (Story 6.4, D7).
   *
   * Before this story `error` was one `useState` slot and a listing START
   * wrote `null` into it, so "loading earlier messages failed" came down on
   * the next 10 s clock tick rather than living until the next success. With
   * the older-page and first-load errors DERIVED from the query, they would
   * survive in the cache instead -- the banner would sit there through every
   * tick, which is a behaviour change nobody asked for.
   *
   * So the error that was showing when a listing started is remembered and
   * suppressed. Error objects are unique per failure, so a NEW failure after
   * the tick is a different object and shows normally; only the one the tick
   * cleared stays down.
   */
  const [suppressedMessagesError, setSuppressedMessagesError] =
    useState<unknown>(null);
  /**
   * ⚠️ THE RISING EDGE ONLY (code review, 2026-09-17). The guard used to be
   * `if (!isFetching && !listingAsked) return`, which skips only when BOTH are
   * false -- so the effect also ran when `isFetching` went true -> false while
   * `listingAsked` was still raised, a window this file documents as real at
   * the listing coalesce above. It then captured whatever error was showing at
   * that moment rather than the one showing when a listing STARTED, and since
   * the slot is compared by identity and never cleared, that error was hidden
   * for good: banner gone, cursor and control still there, a re-press into
   * silence. D7's pin exercises the rising edge only, which is why it stayed
   * green.
   */
  const listeningWasAsked = useRef(false);
  useEffect(() => {
    const asked = threadsState.isFetching || listingAsked;
    const rising = asked && !listeningWasAsked.current;
    listeningWasAsked.current = asked;
    if (!rising) return;
    setSuppressedMessagesError(messagesStateErrorRef.current);
  }, [threadsState.isFetching, listingAsked]);

  /**
   * ⚠️ THE OLDER-PAGE ERROR AND THE FIRST-LOAD ERROR SURFACE; A REFETCH
   * ERROR DOES NOT. That is today's split, reproduced rather than redesigned:
   * `loadOlderMessages` wrote its failure to the shared slot, while the post's
   * re-fetch and the clock refresh each swallowed theirs. `isFetchNextPageError`
   * and `isError && data === undefined` are the library's names for the two
   * that spoke.
   *
   * Suppressed while a fetch is in flight, the same rule the threads error
   * follows since 6.3's review: the banner speaks when an attempt has finished
   * and failed, never while the next one is on the wire.
   */
  const messagesErrorText = (() => {
    if (messagesState.isFetching) return null;
    const e = messagesState.error;
    if (!e || e === suppressedMessagesError) return null;
    if (messagesState.isFetchNextPageError) {
      return `${OLDER_PAGE_ERROR_PREFIX}${describe(e)}`;
    }
    if (messagesState.isError && messagesData === undefined) {
      return `loading messages: ${describe(e)}`;
    }
    return null;
  })();

  /**
   * ⚠️ ONE `error` SLOT, SHARED WITH EVERY WRITER, THE WRITERS FIRST. Each
   * query owns its own error; the hook owns the writers'. A refused write is
   * the one the user just caused, so it takes precedence while it is set; the
   * effect above takes it down when the next listing starts.
   *
   * The message is built through `describe()` like every other catch in this
   * file -- the lazy-Hermes guard is about where a catch parameter is READ,
   * and a query error is no different once it reaches a closure.
   */
  const error =
    state.error ??
    messagesErrorText ??
    (threadsError
      ? `loading ${space} threads: ${describe(threadsError)}`
      : null);

  /**
   * ⚠️ THE CURSOR IS NEVER READ TO BUILD A REQUEST (Story 6.4, D5).
   * `getNextPageParam` hands the last page's `nextCursor` to `queryFn`
   * verbatim; `olderCursorOf` below is read only to decide "is there anything
   * to ask for", which is what the deleted `olderCursorRef` did.
   *
   * The collapse, the thread guard and the drop are all the library's now:
   * `fetchMeta.fetchMore` is how the observer itself tells an older page from
   * a re-walk, so "a page is already on the wire for THIS thread" is read from
   * the query state rather than from an in-flight ref; and a page that
   * resolves after the user left lands on the previous KEY, which the switch
   * effect has removed.
   *
   * ⚠️ `fetchNextPage`'s DEFAULT `cancelRefetch: true` IS DELIBERATE
   * (`queryObserver.js:168-176`). A press that arrives while a post's re-walk
   * is in flight FETCHES THE PAGE rather than silently joining the re-walk;
   * the reconcile is lost until the next clock move. Today the press proceeds
   * and its page is DROPPED by the generation compare, so the cost is equal --
   * the transport takes no `AbortSignal`, so today's dropped page and
   * tomorrow's cancelled re-walk are each one wasted round trip -- but the
   * press is now honoured rather than wasted.
   */
  const loadOlderMessages = useCallback(async (): Promise<void> => {
    const threadId = openThreadIdRef.current;
    if (!threadId) return;
    const key = correspondenceMessagesKeys.thread(
      threadId,
      viewerIdRef.current,
    );
    const queryState = queryClient.getQueryState<MessagePages>(key);
    // No page yet, or the beginning reached: nothing to ask for.
    if (!queryState?.data) return;
    if (olderCursorOf(queryState.data) === null) return;
    // A page already on the wire for THIS thread: join it, do not restart it.
    if (
      queryState.fetchStatus === 'fetching' &&
      (queryState.fetchMeta as { fetchMore?: unknown } | undefined)?.fetchMore
    ) {
      return;
    }
    await fetchNextPage();
  }, [queryClient, fetchNextPage]);

  /**
   * Client-minted ids for pending rows; never sent to the server.
   *
   * ⚠️ MODULE-SCOPED, NOT A `useRef` (#1069 Story 6.6 code review). A `useRef(0)`
   * has the HOOK's lifetime, and since this story the overlay and the mutation
   * cache that the id keys into have the CLIENT's. The docked panel unmounts
   * mid-upload (`CorrespondenceDock.tsx:342-345`), so the reopened panel's
   * counter restarted at 0 and its first post minted an id the still-in-flight
   * post already owned -- measured: two posts, `ids: ["pending-1","pending-1"]`.
   * `seed` then overwrote the first post's `abort` and `files`, `cancelPending`
   * aborted the wrong upload, and `MessageList.tsx:577` rendered both rows under
   * one React key. The id's scope has to be at least the store's; a module
   * counter is monotonic for the life of the JS context, which is wider than any
   * client, so it is safe for both the app singleton and a per-test client.
   */
  // ⚠️ `abortsRef`, `filesRef` AND `forgetPost` LEFT IN STORY 6.6. All three
  // held something for the life of a post in flight, and all three had the
  // HOOK's lifetime -- which the docked panel ends mid-upload
  // (`CorrespondenceDock.tsx:342-345`). They are `uploadOverlay`'s now, keyed
  // on the `QueryClient`, with one `forget` for the bar, the handle and the
  // bytes together, exactly as `forgetPost` was one function for the last two.

  // ⚠️ `mergeNewestPage` AND `pageCoversScreen` MOVED TO
  // `correspondenceQueries.ts` (Story 6.4, D1, D8) as `foldNewestPage` and
  // `pageCoversScreen`: pure, unit-tested, and stated over the cache's pages
  // rather than over a closure on `State`. Both callers now take the list and
  // the fold from ONE cache snapshot, which is what closes the
  // `messagesRef`-vs-queued-state gap the 2026-09-11 second pass recorded.

  /** AC 10: how long before the pending row admits it is taking a while. */
  const PENDING_SLOW_AFTER_MS = 10_000;

  /**
   * Move one in-flight post's row (the UI half of the join, Story 6.6).
   *
   * ⚠️ IT WRITES `uploadOverlay`, NOT STATE. `setPending` and `removePending`
   * left with `State.pending`: the cache's own `pending`/`success`/`error`
   * dispatches add and drop the row now, and the only thing left to write is
   * how far along the upload is. A patch for a post that has already settled
   * is a no-op and does not notify.
   */
  const patchPending = useCallback(
    (localId: string, fields: { percent?: number | null; slow?: boolean }) =>
      uploadOverlay.patch(queryClient, localId, fields),
    [queryClient],
  );

  /**
   * What every writer's `onError` says, in the order it has to say it.
   *
   * ⚠️ THE READ-ONLY CHECK COMES FIRST, AND THE SENTENCE IS THE TOAST'S OWN
   * (Story 6.5, D7). Since the writers became mutations the app-wide
   * `MutationCache.onError` (`src/lib/queryClient.ts:181-185`) sees a
   * read-only impersonation refusal and toasts for it, exactly as it does for
   * every other write in the product. The toast answers "why is the app
   * refusing me"; the line under the composer answers "did my message go".
   * Two channels, one concern each -- and they must never word it
   * differently, so the line reads the toast's constant rather than the
   * server's text in that one case.
   *
   * ⚠️ THE ERROR IS READ INTO LOCALS BEFORE ANY CLOSURE IS CREATED, the rule
   * `describe()` states for every catch in this file. An `onError` parameter
   * is not a catch binding and is not the lazy-Hermes shape, but the file
   * keeps one rule rather than two.
   */
  const reportWriteFailure = useCallback(
    (verb: string, e: unknown) => {
      const readOnly = isReadOnlyViolationError(e);
      /**
       * ⚠️ THE READ-ONLY SENTENCE GOES IN **BOTH** SLOTS (code review
       * 2026-09-17, decision 2). D7 said "two channels, one concern each, and
       * they must never word it differently", and the review found there are
       * THREE things on screen, not two: the toast, the composer line, and the
       * "Discussion error" bar, which is rendered from `error`
       * (`CorrespondencePanel.tsx:1833-1842`). `error` was built from
       * `describe(e)` unconditionally, so a read-only impersonator saw the
       * product's sentence twice and `IMPERSONATION_READ_ONLY_VIOLATION: writes
       * are not allowed` once.
       *
       * ⚠️ AND THIS IS NOT AN EXCEPTION TO AC 37. AC 37 exists so a 403's
       * explanation and a 400's reason reach the user; for this ONE refusal the
       * server's "explanation" is an internal code, so showing the product's own
       * sentence serves AC 37's intent rather than overriding it. Every other
       * error still carries the server's text verbatim, which the ordinary-403
       * test next to the D7 test pins.
       */
      const message = readOnly ? READ_ONLY_VIOLATION_MESSAGE : describe(e);
      const refusal = readOnly ? READ_ONLY_VIOLATION_MESSAGE : refusalText(e);
      setState(s => ({
        ...s,
        error: `${verb}: ${message}`,
      }));
      setWriteRefusal(refusal, refusedMentionIdsOf(e));
    },
    [setWriteRefusal],
  );
  /**
   * Call off a post in flight (#1069 Story 4.1, AC 11, FR18).
   *
   * Aborting makes the upload reject with `aborted: true`, which the post's
   * `onError` turns into a silent removal and a reconcile. Nothing is written
   * server-side.
   *
   * ⚠️ WHERE THE HANDLE COMES FROM SINCE STORY 6.5: the post mutation's
   * function calls `onUploadStarted` with it BEFORE it returns the upload
   * promise, and the wrapper's `onUploadStarted` is what registers it. That
   * ordering is the whole point -- a cancel pressed in the first moments must
   * find a handle -- and it is pinned twice, at the module and through this
   * function.
   *
   * ⚠️ AND THE HANDLE IS THE CLIENT'S SINCE STORY 6.6, not this instance's. A
   * docked panel closed mid-upload unmounts the hook that started the post; the
   * Cancel the REOPENED panel renders has to reach that same upload.
   */
  const cancelPending = useCallback(
    (localId: string) => uploadOverlay.abort(queryClient, localId),
    [queryClient],
  );

  /** The thread whose newest page is being refreshed, so overlaps collapse. */
  const refreshInFlight = useRef<string | null>(null);
  /**
   * A refresh asked for while one is in flight is COALESCED, not dropped
   * (second pass, 2026-09-11): the in-flight page was requested before the
   * move that asked again, and the panel has already advanced the clock it
   * holds, so a dropped ask lost that move exactly as `reloadThreads` once
   * did. One follow-up when the flight settles, however many asks arrived.
   */
  const refreshRerunRequested = useRef(false);
  const refreshOpenThreadRef = useRef<() => Promise<void>>(
    async () => undefined,
  );

  const refreshOpenThread = useCallback(async (): Promise<void> => {
    const threadId = openThreadIdRef.current;
    if (!threadId) return;
    // Two refreshes of one thread inside one slow round trip would both pass
    // the guards below and the later-RESOLVING one would win regardless of
    // which was issued last (code review 2026-09-11). Same shape as
    // `olderInFlight`, plus the follow-up.
    if (refreshInFlight.current === threadId) {
      refreshRerunRequested.current = true;
      return;
    }
    refreshInFlight.current = threadId;
    refreshRerunRequested.current = false;
    // Observe, do not bump: like `loadOlderMessages`, a refresh must not
    // cancel a newer load, but a newer load (a post's re-walk) must cancel
    // THIS.
    const generation = loadGeneration.current;
    // Captured before the round trip; see the mark-read effect's note.
    const keyAtRefreshStart = threadsKey_;
    const messagesKey = correspondenceMessagesKeys.thread(
      threadId,
      viewerIdRef.current,
    );
    try {
      // ⚠️ ONE DIRECT REQUEST, NOT AN INVALIDATION (Story 6.4, C7).
      // See the interface docblock: `refetch` on an infinite query re-walks
      // every loaded page, which would cost one request per "Load earlier" the
      // reader has pressed, on every clock move, and would refresh history
      // that must not be marked read.
      const page = await provider.listMessages(threadId);
      if (
        openThreadIdRef.current !== threadId ||
        generation !== loadGeneration.current
      ) {
        return;
      }
      /**
       * ⚠️ THE SCREEN IS READ FROM THE CACHE, ONCE (Story 6.4, D8).
       * The empty-page guard and the covered test used to read `messagesRef`
       * -- the last RENDERED list -- while the merge read the queued state, so
       * they could disagree when an older page was queued in the same flush.
       * One snapshot decides both, and it is the same snapshot the fold is
       * about to be applied to.
       */
      const onScreen = messagesInReadingOrder(
        queryClient.getQueryState<MessagePages>(messagesKey)?.data,
      );
      // An EMPTY newest page for a conversation that HAS messages is nothing
      // to fold: replacing the list with it would blank the conversation and
      // then mark it read (second pass). An empty page for an empty screen
      // changes nothing and may mark.
      if (page.items.length === 0 && onScreen.length > 0) return;
      // Decided against the list BEFORE the fold changes it.
      const covered = pageCoversScreen(page, onScreen);
      patch(messagesKey, foldNewestPage(page));
      // ⚠️ THE THIRD SITE, AND THE EPIC DID NOT NAME IT (Story 7.4, C2/D6).
      // This is the clock path: it folds ANOTHER PERSON's newest page into
      // the list, so a file somebody else just posted appears in a bubble
      // while the strip and its count sit stale — the one case where the
      // reader can SEE the disagreement. It is bounded by the same clock that
      // already pays for the `listMessages` above it.
      //
      // ⚠️ GATED ON THE PAGE, NOT FIRED ON EVERY TICK (code review,
      // 2026-09-18). An EDIT moves `last_activity_at` too
      // (`correspondence.service.ts`'s `touchLastActivity`), and the same
      // service is the record that files survive an edit untouched — so
      // unconditionally, a typo fix refetched a 500-file listing in every
      // open panel, which is the cost `postMut` is gated to avoid.
      //
      // BOTH HALVES ARE REQUIRED. `attachments` is the arrival of a file;
      // `deleted` is its departure, and it cannot be folded into the first,
      // because a withdrawal takes the files with the body at the SQL and the
      // tombstone that comes back carries no `attachments` at all (NFR50).
      if (page.items.some(m => (m.attachments?.length ?? 0) > 0 || m.deleted)) {
        invalidateThreadFiles(threadId);
      }
      // D6: rendered, so read. ONLY ON SUCCESS, ONLY WHEN THE PAGE COVERED
      // THE SCREEN (review decision 1, 2026-09-11: a corrected body above the
      // page is what the reader has NOT seen, so the dot stays), and
      // swallowed on failure, for the reasons the open-thread effect gives.
      if (!covered) return;
      provider
        .markThreadRead(threadId)
        .then(() => {
          onThreadReadRef.current?.();
          // The key captured when this refresh started; see the open-thread
          // effect's note on why it is not the live one, and on `rows?.map`.
          // #1599 Story 1.5: the mention badge clears with the dot here too,
          // by the open-thread effect's rule (absent key stays absent).
          queryClient.setQueryData<Thread[]>(keyAtRefreshStart, rows =>
            rows?.map(t =>
              t.id === threadId && (t.unread || t.mentionsViewer)
                ? {
                    ...t,
                    unread: false,
                    ...(t.mentionsViewer === undefined
                      ? {}
                      : { mentionsViewer: false }),
                  }
                : t,
            ),
          );
        })
        .catch(() => {
          // Deliberately swallowed; see the open-thread effect.
        });
    } catch {
      // A failed refresh keeps the list and reports no error: the list on
      // screen is still true, only older than it could be. THIS move is not
      // retried; a later move asks again.
    } finally {
      if (refreshInFlight.current === threadId) {
        refreshInFlight.current = null;
        if (refreshRerunRequested.current) {
          refreshRerunRequested.current = false;
          refreshOpenThreadRef.current().catch(() => {
            // The follow-up swallows its own failure, as this one did.
          });
        }
      }
    }
    // `invalidateThreadFiles` is listed because `exhaustive-deps` requires
    // every callable this body names, and it is now `[queryClient]`-stable —
    // so listing it costs nothing.
    //
    // ⚠️ **[CORRECTED at code review, 2026-09-18]** this said the dependency
    // was load-bearing BECAUSE `invalidateThreadFiles` closes over the
    // `viewerId` VALUE. It did, and that was the bug, not the justification:
    // the line directly above every call site keys its patch off
    // `viewerIdRef.current`, and this file's own note on that ref says a
    // closure over the value "would be the first render's forever". The
    // invalidation now reads the ref too, so the patch and the invalidation
    // it sits beside can no longer name two different people.
  }, [provider, queryClient, threadsKey_, patch, invalidateThreadFiles]);
  useEffect(() => {
    refreshOpenThreadRef.current = refreshOpenThread;
  }, [refreshOpenThread]);

  // ==========================================================================
  // The four writers (Story 6.5). Each is a `useMutation` over the options in
  // `correspondenceQueries.ts`; the public members below are wrappers over
  // `mutateAsync` that keep their old signatures and outcome mapping.
  //
  // ⚠️ EVERY SIDE EFFECT IS IN THESE HOOK-LEVEL OPTIONS, AND NOTHING IS PASSED
  // TO `mutateAsync` AS PER-CALL OPTIONS. The panel allows more than one post
  // in flight and `useMutation` is ONE observer: `mutate()` detaches it from
  // the previous mutation (`mutationObserver.js:56-61`), whose hook-level
  // callbacks still run because they live on the `Mutation`, and whose
  // per-call callbacks are skipped because `#notify` fires those only while
  // the observer still holds them (`:74-127`). A cleanup in a per-call option
  // would therefore be silently dropped for every post but the last.
  // For the same reason `isPending` is the LAST call's and is never read.
  //
  // ⚠️ THE CALLBACKS ARE AWAITED BEFORE `mutateAsync` RESOLVES
  // (`mutation.js:123-144`), which is what keeps `await postMessage()` meaning
  // "the re-walk is done" for the panel and for the suites.
  //
  // ⚠️ NO `cancelQueries` IN `onMutate`, which is the one part of the house
  // optimistic-update shape (`src/hooks/mutations/useUpdateTeamMembers.ts`)
  // this hook does not copy: a reverted cancel is reported by the test
  // harness's teardown guard as a fetch that never settled. The post's
  // `refetchQueries(..., { cancelRefetch: true })` below is the library's own
  // silent cancel plus a replacement fetch, which balances.
  // ==========================================================================

  const createThreadMut = useMutation({
    ...createThreadMutation({ provider }),
    onMutate: () => {
      setWriteRefusal(null);
    },
    /**
     * ⚠️ THE INVALIDATION IS AWAITED HERE, WHICH IS WHAT MAKES THE RETURNED ID
     * USABLE (Story 6.3, C12). `invalidateQueries` resolves after the refetch
     * settles, so the new row is in the list before the wrapper hands its id
     * back and the panel opens a conversation whose header is already right.
     * `onSuccess` is awaited before `mutateAsync` resolves, so moving it here
     * changed nothing about that ordering.
     */
    onSuccess: async () => {
      await reloadThreads();
    },
    onError: (e: Error) => {
      if ((e as { unconfirmed?: boolean } | null)?.unconfirmed) {
        // #1530 review, C1: a first message with files rides the progress
        // transport, and a response-phase stall means the conversation may
        // already exist. Reconcile the LISTING (the create's own cache) the
        // way the post reconciles its thread, and still report the failure:
        // the wrapper hands the panel `'unconfirmed'`.
        reloadThreads().catch(() => {});
      }
      reportWriteFailure('creating thread', e);
    },
  });

  const postMut = useMutation({
    ...postMessageMutation({
      provider,
      takeFiles: localId => uploadOverlay.takeFiles(queryClient, localId),
    }),
    /**
     * OPTIMISTIC (Story 1.3, AC 17): the message renders as a pending row on
     * the viewer's side BEFORE the call.
     *
     * ⚠️ IT NO LONGER ADDS THE ROW (Story 6.6). The library's own `pending`
     * dispatch IS the row: this callback is awaited before the mutation
     * function runs, and the cache already holds the mutation by then. Nor is
     * there an open-thread check here any more -- a post to a thread the user
     * is not looking at is filtered out by `toPendingRows` and comes BACK when
     * they return to it, which is the point of the story.
     */
    onMutate: (v: PostMessageVariables) => {
      const carriesFiles = v.fileCount > 0;
      setWriteRefusal(null);
      // AC 10's third line. Scheduled, never a spinner on a timer: the row
      // only says "still sending" once it genuinely has been.
      const slowTimer = carriesFiles
        ? setTimeout(
            () => patchPending(v.localId, { slow: true }),
            PENDING_SLOW_AFTER_MS,
          )
        : null;
      return { slowTimer };
    },
    onSuccess: async (
      message: Message,
      v: PostMessageVariables,
      context: { slowTimer: ReturnType<typeof setTimeout> | null } | undefined,
    ) => {
      if (context?.slowTimer) clearTimeout(context.slowTimer);
      uploadOverlay.forget(queryClient, v.localId);
      // The server's message replaces the pending row, and the append is
      // also the FALLBACK if the re-fetch below fails: the message is
      // already delivered, so showing it is right either way.
      //
      // ⚠️ GUARDED BY THE SAME REF AS THE RE-FETCH. Unguarded, a post that
      // resolves after the user has switched threads (or closed the panel)
      // appends thread A's message to thread B's visible list.
      if (openThreadIdRef.current === v.threadId) {
        patch(
          correspondenceMessagesKeys.thread(v.threadId, viewerIdRef.current),
          appendMessage(message),
        );
        // ⚠️ ONLY WHEN THE POST ACTUALLY CARRIED FILES (Story 7.4, D6). A
        // text-only message changes nothing the listing shows, and an
        // invalidation with `staleTime: 0` and a mounted observer is a real
        // round trip — on every message anyone ever sends.
        //
        // The server's reply is what is asked, not the variables: `attachments`
        // is what the server stored, so a payload the write path refused never
        // triggers a refetch of a listing that did not change.
        if (message.attachments && message.attachments.length > 0) {
          invalidateThreadFiles(v.threadId);
        }
      } else {
        // ⚠️ LEFT WHILE OFFLINE, THE CONVERSATION IS KEPT, AND SO IS THIS
        // (review, 2026-09-26). A post that lands after the user left a
        // conversation offline belongs in that conversation's kept pages, or
        // a return while still offline shows it without the user's own
        // delivered message. Only a kept entry: an entry that was removed
        // online is refetched on return and needs nothing.
        patchKept(
          correspondenceMessagesKeys.thread(v.threadId, viewerIdRef.current),
          appendMessage(message),
        );
      }
      // ⚠️ NOTHING REMOVES THE ROW HERE (Story 6.6): the library's `success`
      // dispatch takes the mutation out of `status: 'pending'` and the
      // derivation stops emitting it.
      const generation = ++loadGeneration.current;

      /**
       * ⚠️ RE-FETCH, AND IT IS NOT POLISH (#1069 P3.1).
       *
       * Posting a message marks the whole thread read for the author, which
       * is what "your own message never marks a thread unread for you"
       * (FR56) reduces to now that there are no counts. That is only
       * defensible if the messages it acknowledges were actually ON SCREEN.
       * Measured 2026-08-25: they were not. The panel loads messages exactly
       * once per thread-open and has no interval, no refetch-on-focus and no
       * subscription anywhere, so a message posted by somebody else while the
       * user was composing was never rendered at all, and the post then
       * silently marked it read.
       *
       * ⚠️ ASKED OF THE LIBRARY, WITH `cancelRefetch: true` (Story 6.4, D2,
       * C7): that flag is the library's own "the last-started load wins",
       * which is what `loadGeneration` bought by hand here. Its cost is one
       * request per loaded page, recorded rather than hidden.
       *
       * Fire-and-forget and swallowed on failure: the library keeps `data`
       * through a failed refetch, so the optimistic append stands. A stale
       * list is better than an error banner over a message that was delivered.
       */
      try {
        // Only if the user is still looking at this thread AND no newer load
        // has started.
        //
        // ⚠️ FROM A RETIRED MOUNT, JOIN; NEVER CANCEL (review, 2026-09-26).
        // `generation` and `loadGeneration` are this mount's, and a mount a
        // resize retired sees neither the live mount's loads nor its bumps, so
        // its guard always passed and `cancelRefetch: true` cancelled the live
        // mount's in-flight older page. Retired, the re-walk joins whatever is
        // in flight instead; the optimistic append already stands.
        const live = mountedRef.current;
        if (
          openThreadIdRef.current === v.threadId &&
          (!live || generation === loadGeneration.current)
        ) {
          await queryClient.refetchQueries(
            {
              queryKey: correspondenceMessagesKeys.thread(
                v.threadId,
                viewerIdRef.current,
              ),
              exact: true,
            },
            { cancelRefetch: live },
          );
        }
      } catch {
        // Keep the optimistic append. See above.
      }
    },
    onError: (
      e: Error,
      v: PostMessageVariables,
      context: { slowTimer: ReturnType<typeof setTimeout> | null } | undefined,
    ) => {
      if (context?.slowTimer) clearTimeout(context.slowTimer);
      uploadOverlay.forget(queryClient, v.localId);
      // ⚠️ A CANCEL IS NOT A FAILURE (AC 11 vs AC 13). The user asked for
      // this, so the row goes and NOTHING is said: no error banner, no
      // unsent-message line. The message and its files are already back in
      // the composer, because the composer never gave them up (AC 12).
      if ((e as { aborted?: boolean } | null)?.aborted) {
        // The row goes with the `error` dispatch, as it does on every other
        // exit path since Story 6.6.
        /**
         * ⚠️ AN ABORT IS NOT PROOF THAT NOTHING WAS WRITTEN (code review
         * 2026-09-13). `xhr.abort()` tears down THIS END of the connection. A
         * cancel pressed while the bar has sat at 100% -- which is exactly
         * when people press it -- can land after the body has fully arrived
         * and while the server is inside its transaction, so the message
         * commits. Saying nothing then leaves the user believing they stopped
         * it, and pressing Post again delivers it twice.
         *
         * So RECONCILE rather than assert: re-fetch the newest page. If the
         * post did land the user simply sees it and can withdraw it; if it did
         * not, nothing appears and the silence AC 11 asks for is intact. The
         * general answer is an idempotency key, which has no precedent in this
         * product; it is deferred, and it is also why these mutations set
         * `retry: false` (Story 6.5, C2).
         *
         * ⚠️ OBSERVE THE GENERATION, DO NOT BUMP IT (code review run 2): only
         * the open-thread load and the post-success path bump. Bumping here
         * cancelled an in-flight edit's fold, and `editMessage` still returned
         * `true` over a body still showing the old text.
         *
         * ⚠️ `refreshOpenThread`, NOT A SECOND COPY OF IT (run 3): the copy
         * had its own generation check and its own empty-page guard and sat
         * beside the coalescer that exists because two refreshes of one thread
         * inside one slow round trip would both pass the guards and the
         * later-RESOLVING one would win regardless of which was issued last.
         *
         * Fire-and-forget and swallowed on failure: the user has just
         * cancelled and an error banner about a refresh they did not ask for
         * would be noise.
         */
        refreshOpenThreadRef.current().catch(() => {});
        return;
      }
      if ((e as { unconfirmed?: boolean } | null)?.unconfirmed) {
        // The body left this device and nothing came back (#1069 Story 6.7,
        // D3): the server may have committed. Reconcile exactly as the cancel
        // path does (same call, same reasons, see above), and STILL report
        // the failure below: `error` carries the transport's sentence for the
        // banner, and the wrapper hands the panel `'unconfirmed'` so its line
        // is the cautious one rather than "Not sent".
        refreshOpenThreadRef.current().catch(() => {});
      }
      // The pending row goes with the `error` dispatch; the draft is still in
      // the composer, which is the recovery path. The server's own text (a
      // 403's explanation, a 400's reason) reaches the panel through `error`
      // (AC 37).
      reportWriteFailure('posting message', e);
    },
  });

  const editMut = useMutation({
    ...editMessageMutation({ provider }),
    // Observe the generation: an edit that resolves after a newer load (a
    // thread switch, a post's re-fetch) must not paste into it. Captured
    // before the call, exactly where the hand-rolled version captured it.
    onMutate: () => {
      setWriteRefusal(null);
      return { generation: loadGeneration.current };
    },
    onSuccess: async (
      edited: Message,
      v: EditMessageVariables,
      // ⚠️ NOT OPTIONAL, AND NOT `??`-DEFAULTED (code review 2026-09-17).
      // `onMutate` above always returns it and the library always passes it
      // back (`mutation.js:100-107` dispatches the context, `:117-121` hands
      // `this.state.context` to `onSuccess`), so the value is structurally
      // present. It was written `context?.generation ?? loadGeneration.current`,
      // which reads the generation at SUCCESS time when the context is absent
      // -- making `generation !== loadGeneration.current` unsatisfiable and
      // silently DISABLING the stale-fold guard. Unreachable today, which is
      // exactly why nothing would have failed if a future `onMutate` stopped
      // returning it. A guard that fails open wants a type that cannot be
      // omitted, not a fallback.
      context: { generation: number },
    ) => {
      const generation = context.generation;
      const messagesKey = correspondenceMessagesKeys.thread(
        v.threadId,
        viewerIdRef.current,
      );
      // Left while offline: the kept pages take the edit (see the post).
      if (openThreadIdRef.current !== v.threadId) {
        patchKept(
          messagesKey,
          mapMessages(m => (m.id === v.messageId ? edited : m)),
        );
        return;
      }
      if (generation !== loadGeneration.current) return;
      // Read from the CACHE, not from a rendered snapshot (Story 6.4, D8):
      // the same data the patch below is about to be applied to.
      const onScreen = messagesInReadingOrder(
        queryClient.getQueryState<MessagePages>(messagesKey)?.data,
      ).some(m => m.id === v.messageId);
      patch(
        messagesKey,
        mapMessages(m => (m.id === v.messageId ? edited : m)),
      );
      /**
       * The row was not in the list (an interleaved reload replaced the page
       * underneath the edit). The server has the edit; `map` was a no-op;
       * returning `true` alone would report a save the screen does not show
       * (review). Re-fetch the newest page the way the post does, under the
       * same guards, and swallow a failure the same way.
       */
      if (onScreen) return;
      try {
        if (
          openThreadIdRef.current === v.threadId &&
          generation === loadGeneration.current
        ) {
          await queryClient.refetchQueries(
            { queryKey: messagesKey, exact: true },
            { cancelRefetch: true },
          );
        }
      } catch {
        // The edit is saved; a stale list is better than an error over it.
      }
    },
    onError: (e: Error) => {
      if ((e as { unconfirmed?: boolean } | null)?.unconfirmed) {
        // #1530 review, C1: an edit that ADDS files is a PATCH over the
        // progress transport; a response-phase stall means the server may
        // have committed it. Reconcile the open thread exactly as the post's
        // cancel and unconfirmed paths do (same call, same reasons), and
        // still report: the wrapper hands the panel `'unconfirmed'`.
        refreshOpenThreadRef.current().catch(() => {});
      }
      reportWriteFailure('editing message', e);
    },
  });

  const deleteMut = useMutation({
    ...deleteMessageMutation({ provider }),
    onMutate: () => {
      setWriteRefusal(null);
      return { generation: loadGeneration.current };
    },
    onSuccess: (
      _void: void,
      v: DeleteMessageVariables,
      // Non-optional for the reason the edit's `onSuccess` states above.
      context: { generation: number },
    ) => {
      const generation = context.generation;
      // The tombstone the server would return on the next read: author and
      // time kept, body empty, no `editedAt`, `deleted: true`.
      const toTombstone = mapMessages(m => {
        if (m.id !== v.messageId) return m;
        // The withdrawal takes the files with the body (NFR50); the server's
        // own tombstone carries neither. It names nobody either (#1599: the
        // server's tombstone carries `mentions: []`).
        const { editedAt: _editedAt, attachments: _attachments, ...rest } = m;
        return { ...rest, body: '', deleted: true as const, mentions: [] };
      });
      const messagesKey = correspondenceMessagesKeys.thread(
        v.threadId,
        viewerIdRef.current,
      );
      // Left while offline: the kept pages take the withdrawal (see the post).
      if (openThreadIdRef.current !== v.threadId) {
        patchKept(messagesKey, toTombstone);
        return;
      }
      if (generation !== loadGeneration.current) return;
      patch(messagesKey, toTombstone);
      // ⚠️ ALWAYS, UNLIKE THE POST (Story 7.4, D6). The withdrawal takes the
      // files with the body at the SQL, so a message that HAD files must lose
      // them from the strip — and the tombstone patch above has already
      // thrown `attachments` away, so this callback cannot tell afterwards
      // whether there were any. Asking the listing is one round trip on an
      // action a person takes by hand; guessing is a strip that keeps showing
      // a withdrawn drawing.
      invalidateThreadFiles(v.threadId);
    },
    onError: (e: Error) => reportWriteFailure('deleting message', e),
  });

  /**
   * The fifth writer (Story 7.3, FR74).
   *
   * ⚠️ IT INVALIDATES, IT DOES NOT PATCH. The thread header and the thread row
   * both render `title` off the THREADS query, so the awaited `reloadThreads()`
   * IS the "show the new title" mechanism -- the same shape `createThreadMut`
   * uses, and the reason there is no optimistic title to roll back on a
   * refusal. No `onSettled` invalidation: re-fetching a list after a failure
   * re-fetches a list that has not changed.
   */
  const renameThreadMut = useMutation({
    ...renameThreadMutation({ provider }),
    onMutate: () => {
      setWriteRefusal(null);
    },
    onSuccess: async () => {
      await reloadThreads();
    },
    onError: (e: Error) => reportWriteFailure('renaming conversation', e),
  });

  // --------------------------------------------------------------------------
  // The pending rows (#1069 Story 6.6). No state, two reads and a join.
  // --------------------------------------------------------------------------
  /**
   * Every post still on the wire, for the whole client. A CACHE READ: it issues
   * no request, and it publishes one macrotask late through `notifyManager`
   * exactly as the two queries do (`useMutationState.js:39-42`).
   */
  const inFlightPosts = useMutationState(inFlightPostsOptions());
  /**
   * How far along each of them is. `useSyncExternalStore` rather than state
   * because the store outlives this hook on the docked path; `snapshot` returns
   * the same `Map` instance until a write, which is what stops it looping.
   */
  const overlay = useSyncExternalStore(
    useCallback(
      (listener: () => void) => uploadOverlay.subscribe(queryClient, listener),
      [queryClient],
    ),
    useCallback(() => uploadOverlay.snapshot(queryClient), [queryClient]),
  );
  /**
   * The join, and the open-thread filter WITH IT -- in render, for the reason
   * the header gives (a thread switch is not a mutation-cache event).
   * `openThreadId` the STATE, not the ref: the ref is for callbacks that have
   * already awaited, this is a render.
   */
  const pending = useMemo(
    () => toPendingRows(inFlightPosts, openThreadId, overlay),
    [inFlightPosts, openThreadId, overlay],
  );

  // --------------------------------------------------------------------------
  // The public writers. Wrappers over `mutateAsync` with NO per-call options:
  // they map a rejection onto the outcome the panel reads and nothing else.
  // `mutateAsync` is the observer's bound `mutate` and is stable across
  // renders (`useMutation.js:40`), so these stay referentially stable too.
  // --------------------------------------------------------------------------

  const createThreadAsync = createThreadMut.mutateAsync;
  const createThread = useCallback(
    async (
      title: string,
      body: string,
      files: OutgoingMessageFile[] = [],
    ): Promise<CreateThreadResult> => {
      try {
        const thread = await createThreadAsync({
          requestId,
          space,
          title,
          body,
          ...(files.length > 0 ? { files } : {}),
        });
        return { outcome: 'sent', id: thread.id };
      } catch (e) {
        // `onError` has already written the banner and the refusal.
        if ((e as { unconfirmed?: boolean } | null)?.unconfirmed === true) {
          return { outcome: 'unconfirmed' };
        }
        return { outcome: 'failed' };
      }
    },
    [createThreadAsync, requestId, space],
  );

  const postAsync = postMut.mutateAsync;
  const postMessage = useCallback(
    async (
      threadId: string,
      body: string,
      files: OutgoingMessageFile[] = [],
    ): Promise<PostOutcome> => {
      const localId = `pending-${(nextLocalIdSeq += 1)}`;
      // ⚠️ BEFORE `mutate()`, AND THAT ORDERING IS NOW TWO THINGS (Story 6.6,
      // C10). The mutation function reads the bytes from here, as it has since
      // 6.5 -- and the derived row is emitted the moment the cache holds the
      // mutation, so a FILES row would render without its overlay (a bar at 0%
      // that never moved) if the entry arrived later. Both are answered by
      // seeding first. Cleared by `uploadOverlay.forget` on success, failure
      // and cancel alike.
      // ⚠️ EVERY POST, NOT ONLY A FILES POST (#1069 Story 6.6 code review,
      // decision 1). The entry's presence is what `toPendingRows` reads as
      // "still in flight", so a text post needs one too or its row would never
      // be emitted at all. A text entry carries no `files`.
      uploadOverlay.seed(
        queryClient,
        localId,
        files.length > 0
          ? { percent: 0, slow: false, files }
          : { percent: null, slow: false },
      );
      try {
        await postAsync({
          threadId,
          body,
          fileCount: files.length,
          localId,
          onProgress: percent => patchPending(localId, { percent }),
          // AC 11: the handle has to be reachable from `cancelPending` before
          // the first byte goes out, so the mutation function hands it over
          // rather than this wrapper waiting for a promise it cannot see into.
          onUploadStarted: abort =>
            uploadOverlay.patch(queryClient, localId, { abort }),
        });
        return 'sent';
      } catch (e) {
        const flags = e as { aborted?: boolean; unconfirmed?: boolean } | null;
        if (flags?.aborted === true) return 'cancelled';
        // Story 6.7, D3: the last byte left and the server never answered.
        if (flags?.unconfirmed === true) return 'unconfirmed';
        return 'failed';
      }
    },
    [postAsync, patchPending, queryClient],
  );

  const editAsync = editMut.mutateAsync;
  const editMessage = useCallback(
    async (
      threadId: string,
      messageId: string,
      body: string,
      files: OutgoingMessageFile[] = [],
    ): Promise<EditOutcome> => {
      try {
        await editAsync({
          threadId,
          messageId,
          body,
          ...(files.length > 0 ? { files } : {}),
        });
        return 'sent';
      } catch (e) {
        if ((e as { unconfirmed?: boolean } | null)?.unconfirmed === true) {
          return 'unconfirmed';
        }
        return 'failed';
      }
    },
    [editAsync],
  );

  const deleteAsync = deleteMut.mutateAsync;
  const deleteMessage = useCallback(
    async (threadId: string, messageId: string): Promise<boolean> => {
      try {
        await deleteAsync({ threadId, messageId });
        return true;
      } catch {
        return false;
      }
    },
    [deleteAsync],
  );

  const renameAsync = renameThreadMut.mutateAsync;
  const renameThread = useCallback(
    async (threadId: string, title: string): Promise<boolean> => {
      try {
        await renameAsync({ threadId, title });
        return true;
      } catch {
        return false;
      }
    },
    [renameAsync],
  );

  return {
    ...state,
    // Derived from the mutation cache and the overlay on every render, since
    // Story 6.6; nothing writes it.
    pending,
    // Derived from the two queries on every render; nothing writes them.
    threads,
    threadsKey,
    loadingThreads,
    messages,
    threadFiles,
    olderMessagesCursor,
    loadingMessages,
    loadingOlder,
    error,
    provider,
    reloadThreads,
    openThread: setOpenThreadId,
    openThreadId,
    createThread,
    postMessage,
    cancelPending,
    editMessage,
    deleteMessage,
    renameThread,
    loadOlderMessages,
    refreshOpenThread,
    reloadThreadFiles,
  };
};
